import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Input, 
  Button, 
  Typography, 
  message, 
  Avatar, 

  Empty,
  
  Tooltip,
  Dropdown,
  Select,
  Modal,
  Badge,
  Form,
  Checkbox,
  Spin,
  Tag
} from 'antd';
import { 
  SendOutlined, 
  
  MessageOutlined, 
  SearchOutlined,
  MoreOutlined,
 
  
  PaperClipOutlined,
  SmileOutlined,
 
  SoundOutlined,
  SoundTwoTone,

  SunOutlined,
  CloseOutlined,
  DownloadOutlined,
  MenuOutlined,
  FilterOutlined,
  HeartOutlined,
  StarOutlined,
  HomeOutlined,
  TagOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import EmojiPicker from 'emoji-picker-react';
import { useWebSocketClient } from '../../../../context/websocketContext';
import { useUISettings } from '../../../../context/uiSettingsContext';
import { useSidebar } from '../../../../context/sidebarContext';
import { useUserContext, canWrite } from '../../../../context/userContext';
import axiosConfig from '../../../../config/axiosConfig';
import chatBgLight from '../../../../assets/images/chat-section_background_light.jpg';
import chatBgDark from '../../../../assets/images/chat-section_background_dark.jpg';
import messageOSLogo from '../../../../assets/images/final-vertical-white (1).png';
import styles from './InboxPage.module.css';

const { Title, Text } = Typography;

// Skeleton shimmer image for "no chat selected" panel
const NoChatLogoImage = ({ src, width, chatTheme }) => {
  const [loaded, setLoaded] = React.useState(false);
  const shimmerBg = chatTheme === 'dark'
    ? 'linear-gradient(90deg, #2a2a2a 25%, #333 50%, #2a2a2a 75%)'
    : 'linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%)';
  return (
    <div style={{ position: 'relative', width, height: width * 0.55, flexShrink: 0 }}>
      {!loaded && (
        <div style={{
          position: 'absolute', inset: 0,
          borderRadius: 16,
          background: shimmerBg,
          backgroundSize: '200% 100%',
          animation: 'inbox-shimmer 1.6s ease-in-out infinite',
        }} />
      )}
      <img
        src={src}
        alt="MessageOS"
        onLoad={() => setLoaded(true)}
        style={{
          width: '100%',
          objectFit: 'contain',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      />
      <style>{`@keyframes inbox-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
    </div>
  );
};

// Check if text is an image URL (supports both direct URLs and signed URLs)
const isImageUrl = (text = '') => {
  if (!text) return false;
  // Extract path before query params for signed URLs
  const urlPath = text.split('?')[0];
  return /\.(jpg|jpeg|png|gif|webp)$/i.test(urlPath);
};

// Check if text is a video URL
const isVideoUrl = (text = '') => {
  if (!text) return false;
  const urlPath = text.split('?')[0];
  return /\.(mp4|mov|avi|webm|mkv|3gp)$/i.test(urlPath);
};

// Check if text is an audio URL
const isAudioUrl = (text = '') => {
  if (!text) return false;
  const urlPath = text.split('?')[0];
  return /\.(mp3|wav|ogg|aac|m4a|opus)$/i.test(urlPath);
};

// Check if text is a document URL
const isDocumentUrl = (text = '') => {
  if (!text) return false;
  const urlPath = text.split('?')[0];
  return /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|csv|zip|rar)$/i.test(urlPath);
};

// Check if text looks like a media path (whatsapp-media/... pattern)
const isMediaPath = (text = '') => {
  if (!text) return false;
  return text.includes('whatsapp-media/') || text.startsWith('whatsapp-media');
};

// Detect media type from path or URL
const detectMediaTypeFromPath = (text = '') => {
  if (!text) return null;
  if (isImageUrl(text) || text.includes('/image/')) return 'image';
  if (isVideoUrl(text) || text.includes('/video/')) return 'video';
  if (isAudioUrl(text) || text.includes('/audio/')) return 'audio';
  if (isDocumentUrl(text) || text.includes('/document/')) return 'document';
  if (text.includes('/sticker/')) return 'sticker';
  return null;
};

// Generate media preview text based on media type (for inbox list display)
const getMediaPreview = (msgType) => {
  switch (msgType) {
    case 'image': return '📷 Photo';
    case 'video': return '🎥 Video';
    case 'audio': return '🎵 Audio';
    case 'document': return '📄 Document';
    case 'sticker': return '😀 Sticker';
    default: return '📎 Media';
  }
};

const isFlowResponseType = (msgType) => String(msgType || '').toLowerCase() === 'flow_response';

const FLOW_RESPONSE_CHAT_SUMMARY = 'User submitted a Flow response';

const getMessageType = (message = {}) => (
  message.msg_type || message.message_type || message.messageType || message.last_msg_type || message.last_message_type || message.lastMessageType || message.msgType
);

const getMessageContent = (message = {}) => (
  message.content ?? message.messageContent ?? message.message_content ?? message.message ?? message.text ?? ''
);

const getLastMessageContent = (message = {}) => (
  message.lastMessage ?? message.lastmessage ?? message.last_message ?? message.last_message_content
  ?? message.messageContent ?? message.message_content ?? message.content ?? message.message ?? ''
);

const isPlainObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

const parseJsonIfPossible = (value) => {
  if (isPlainObject(value) || Array.isArray(value)) return value;
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) return null;

  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
};

const toDisplayText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const formatFlowResponseLabel = (key = '') => String(key || '')
  .replace(/[_-]+/g, ' ')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .trim()
  .replace(/\b\w/g, char => char.toUpperCase());

const flattenFlowResponseValues = (value, prefix = '') => {
  if (!isPlainObject(value) && !Array.isArray(value)) {
    return prefix ? [[prefix, value]] : [];
  }

  return Object.entries(value).flatMap(([key, item]) => {
    if (item === undefined || item === null || item === '') return [];
    const nextPrefix = prefix ? `${prefix}.${key}` : key;
    if (isPlainObject(item) || Array.isArray(item)) {
      const nested = flattenFlowResponseValues(item, nextPrefix);
      return nested.length ? nested : [[nextPrefix, item]];
    }
    return [[nextPrefix, item]];
  });
};

const getFlowResponseJson = (message = {}, rawContent = '') => {
  const containers = [
    rawContent,
    message.content,
    message.messageContent,
    message.message_content,
    message.message,
    message.interactive?.nfm_reply,
    message.nfm_reply,
    message.flowResponse,
    message.flow_response,
    message.flowData,
    message.flow_data,
    message,
  ].filter(Boolean);

  for (const container of containers) {
    const responseValue = container.response_json ?? container.responseJson ?? container.answers ?? container.fields;
    const parsedResponse = parseJsonIfPossible(responseValue);
    if (parsedResponse) return parsedResponse;
    if (isPlainObject(responseValue) || Array.isArray(responseValue)) return responseValue;
  }

  return parseJsonIfPossible(rawContent);
};

const getFlowResponseBody = (message = {}, rawContent = '') => {
  const body = (isPlainObject(rawContent) ? rawContent.body : undefined)
    ?? message.content?.body
    ?? message.messageContent?.body
    ?? message.message_content?.body
    ?? message.message?.body
    ?? message.interactive?.nfm_reply?.body
    ?? message.nfm_reply?.body
    ?? message.body
    ?? message.title
    ?? '';
  if (body) return toDisplayText(body).trim();

  const rawText = typeof rawContent === 'string' ? rawContent.trim() : '';
  if (rawText && !parseJsonIfPossible(rawText)) return rawText;

  return '';
};

const formatFlowResponseContent = (message = {}, rawContent = getMessageContent(message)) => {
  const responseJson = getFlowResponseJson(message, rawContent);
  const bodyText = getFlowResponseBody(message, rawContent);

  if (!responseJson) return toDisplayText(rawContent);

  const lines = [];
  const summaryLine = String(bodyText || '').split(/\r?\n/).map(line => line.trim()).find(Boolean);
  if (summaryLine) lines.push(summaryLine);

  flattenFlowResponseValues(responseJson).forEach(([key, value]) => {
    const label = formatFlowResponseLabel(key);
    if (!label) return;
    lines.push(`${label}: ${toDisplayText(value)}`);
  });

  return lines.length > 0 ? lines.join('\n') : toDisplayText(rawContent);
};

const getDisplayMessageContent = (message = {}, msgType = getMessageType(message)) => {
  const rawContent = getMessageContent(message);
  return isFlowResponseType(msgType) ? formatFlowResponseContent(message, rawContent) : toDisplayText(rawContent);
};

const getDisplayLastMessageContent = (message = {}, msgType = getMessageType(message)) => {
  const rawContent = getLastMessageContent(message);
  return isFlowResponseType(msgType) ? formatFlowResponseContent(message, rawContent) : toDisplayText(rawContent);
};

const getFlowResponsePreview = (text = '') => {
  return FLOW_RESPONSE_CHAT_SUMMARY;
};

const getFlowResponseChatText = () => FLOW_RESPONSE_CHAT_SUMMARY;

// Get display text for last message (handles raw media paths)
const getLastMessageDisplay = (lastMessage, lastMsgType, lastIsMedia, lastMediaPreview) => {
  if (isFlowResponseType(lastMsgType)) {
    return getFlowResponsePreview(lastMessage);
  }

  // If backend provides mediaPreview, use it
  if (lastMediaPreview) return lastMediaPreview;
  
  // If backend indicates it's media, generate preview
  if (lastIsMedia || isMediaType(lastMsgType)) {
    return getMediaPreview(lastMsgType);
  }
  
  // Check if the last_message looks like a media path
  if (lastMessage && isMediaPath(lastMessage)) {
    const detectedType = detectMediaTypeFromPath(lastMessage);
    if (detectedType) {
      return getMediaPreview(detectedType);
    }
    return '📎 Media'; // Fallback for unrecognized media paths
  }
  
  // Regular text message
  return lastMessage || '';
};

// Check if message type is a media type
const isMediaType = (msgType) => {
  return ['image', 'video', 'audio', 'document', 'sticker'].includes(msgType);
};

// Chat Color Themes - 5 themes that work with both light and dark mode
const CHAT_THEMES = {
  whatsapp: {
    name: 'WhatsApp',
    light: {
      sentBubble: '#d9fdd3',
      sentText: '#111b21',
      sentTimestamp: '#667781',
      receivedBubble: '#fff',
      receivedText: '#111b21',
      tickRead: '#53bdeb',
      tickDelivered: '#667781',
      tickSent: '#667781',
      tickPending: '#667781',
      accent: '#25d366',
      headerBg: '#f0f2f5',
    },
    dark: {
      sentBubble: '#005c4b',
      sentText: '#e9edef',
      sentTimestamp: 'rgba(255,255,255,0.6)',
      receivedBubble: '#202c33',
      receivedText: '#e9edef',
      tickRead: '#53bdeb',
      tickDelivered: 'rgba(255,255,255,0.6)',
      tickSent: 'rgba(255,255,255,0.6)',
      tickPending: 'rgba(255,255,255,0.4)',
      accent: '#25d366',
      headerBg: '#202c33',
    },
    avatars: [
      { bg: '#00a884', color: '#fff' },
      { bg: '#53bdeb', color: '#fff' },
      { bg: '#ff9500', color: '#fff' },
      { bg: '#ff2d55', color: '#fff' },
      { bg: '#af52de', color: '#fff' },
      { bg: '#5856d6', color: '#fff' },
      { bg: '#34c759', color: '#fff' },
      { bg: '#ff6b6b', color: '#fff' },
    ]
  },
  ocean: {
    name: 'Ocean Blue',
    light: {
      sentBubble: '#0084ff',
      sentText: '#fff',
      sentTimestamp: 'rgba(255,255,255,0.7)',
      receivedBubble: '#e4e6eb',
      receivedText: '#050505',
      tickRead: '#fff',
      tickDelivered: 'rgba(255,255,255,0.7)',
      tickSent: 'rgba(255,255,255,0.7)',
      tickPending: 'rgba(255,255,255,0.5)',
      accent: '#0084ff',
      headerBg: '#f5f7fb',
    },
    dark: {
      sentBubble: '#0084ff',
      sentText: '#fff',
      sentTimestamp: 'rgba(255,255,255,0.7)',
      receivedBubble: '#3a3b3c',
      receivedText: '#e4e6eb',
      tickRead: '#fff',
      tickDelivered: 'rgba(255,255,255,0.7)',
      tickSent: 'rgba(255,255,255,0.7)',
      tickPending: 'rgba(255,255,255,0.5)',
      accent: '#0084ff',
      headerBg: '#242526',
    },
    avatars: [
      { bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', color: '#333' },
    ]
  },
  sunset: {
    name: 'Sunset',
    light: {
      sentBubble: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      sentText: '#fff',
      sentTimestamp: 'rgba(255,255,255,0.8)',
      receivedBubble: '#fff5f5',
      receivedText: '#2d2d2d',
      tickRead: '#fff',
      tickDelivered: 'rgba(255,255,255,0.8)',
      tickSent: 'rgba(255,255,255,0.8)',
      tickPending: 'rgba(255,255,255,0.6)',
      accent: '#f5576c',
      headerBg: '#fff9fa',
    },
    dark: {
      sentBubble: 'linear-gradient(135deg, #c850c0 0%, #ff5858 100%)',
      sentText: '#fff',
      sentTimestamp: 'rgba(255,255,255,0.8)',
      receivedBubble: '#2d2535',
      receivedText: '#f0e6ef',
      tickRead: '#fff',
      tickDelivered: 'rgba(255,255,255,0.8)',
      tickSent: 'rgba(255,255,255,0.8)',
      tickPending: 'rgba(255,255,255,0.6)',
      accent: '#f5576c',
      headerBg: '#1f1a22',
    },
    avatars: [
      { bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)', color: '#333' },
      { bg: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', color: '#333' },
      { bg: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #ee5a24 0%, #f9ca24 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #d63031 0%, #fd79a8 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #e17055 0%, #fdcb6e 100%)', color: '#fff' },
    ]
  },
  forest: {
    name: 'Forest',
    light: {
      sentBubble: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      sentText: '#fff',
      sentTimestamp: 'rgba(255,255,255,0.8)',
      receivedBubble: '#f0fff4',
      receivedText: '#1a1a1a',
      tickRead: '#fff',
      tickDelivered: 'rgba(255,255,255,0.8)',
      tickSent: 'rgba(255,255,255,0.8)',
      tickPending: 'rgba(255,255,255,0.6)',
      accent: '#11998e',
      headerBg: '#f5fff8',
    },
    dark: {
      sentBubble: 'linear-gradient(135deg, #0f7b6c 0%, #2d9f5d 100%)',
      sentText: '#fff',
      sentTimestamp: 'rgba(255,255,255,0.8)',
      receivedBubble: '#1e2d24',
      receivedText: '#e8f5e9',
      tickRead: '#fff',
      tickDelivered: 'rgba(255,255,255,0.8)',
      tickSent: 'rgba(255,255,255,0.8)',
      tickPending: 'rgba(255,255,255,0.6)',
      accent: '#38ef7d',
      headerBg: '#1a261e',
    },
    avatars: [
      { bg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #00b09b 0%, #96c93d 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #355c7d 0%, #6c5b7b 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #2c3e50 0%, #4ca1af 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #1d976c 0%, #93f9b9 100%)', color: '#fff' },
    ]
  },
  midnight: {
    name: 'Midnight',
    light: {
      sentBubble: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      sentText: '#fff',
      sentTimestamp: 'rgba(255,255,255,0.8)',
      receivedBubble: '#f8f9ff',
      receivedText: '#1a1a2e',
      tickRead: '#fff',
      tickDelivered: 'rgba(255,255,255,0.8)',
      tickSent: 'rgba(255,255,255,0.8)',
      tickPending: 'rgba(255,255,255,0.6)',
      accent: '#667eea',
      headerBg: '#f5f6ff',
    },
    dark: {
      sentBubble: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
      sentText: '#fff',
      sentTimestamp: 'rgba(255,255,255,0.8)',
      receivedBubble: '#1e1e2f',
      receivedText: '#e2e8f0',
      tickRead: '#fff',
      tickDelivered: 'rgba(255,255,255,0.8)',
      tickSent: 'rgba(255,255,255,0.8)',
      tickPending: 'rgba(255,255,255,0.6)',
      accent: '#667eea',
      headerBg: '#161625',
    },
    avatars: [
      { bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #4c51bf 0%, #7c3aed 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #a78bfa 0%, #f472b6 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #818cf8 0%, #c084fc 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #4338ca 0%, #6d28d9 100%)', color: '#fff' },
      { bg: 'linear-gradient(135deg, #7c3aed 0%, #db2777 100%)', color: '#fff' },
    ]
  }
};

// WhatsApp-style tick SVG components
const SingleTick = ({ color = '#667781', size = 16 }) => (
  <svg viewBox="0 0 16 11" height={size} width={size} style={{ display: 'inline-block' }}>
    <path d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.405-2.272a.463.463 0 0 0-.336-.146.47.47 0 0 0-.343.146l-.311.31a.445.445 0 0 0-.14.337c0 .136.047.25.14.343l2.996 2.996a.724.724 0 0 0 .512.203.677.677 0 0 0 .523-.226l6.71-8.418a.468.468 0 0 0 .101-.342.465.465 0 0 0-.153-.31l-.32-.253z" fill={color}></path>
  </svg>
);

const DoubleTick = ({ color = '#53bdeb', size = 18 }) => (
  <svg viewBox="0 0 18 11" height={size} width={size + 2} style={{ display: 'inline-block' }}>
    <path d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.405-2.272a.463.463 0 0 0-.336-.146.47.47 0 0 0-.343.146l-.311.31a.445.445 0 0 0-.14.337c0 .136.047.25.14.343l2.996 2.996a.724.724 0 0 0 .512.203.677.677 0 0 0 .523-.226l6.71-8.418a.468.468 0 0 0 .101-.342.465.465 0 0 0-.153-.31l-.32-.253z" fill={color}></path>
    <path d="M15.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-1.405-1.272-.696.696 2.381 2.38a.724.724 0 0 0 .512.203.677.677 0 0 0 .523-.226l6.71-8.418a.468.468 0 0 0 .101-.342.465.465 0 0 0-.153-.31l-.32-.253z" fill={color}></path>
  </svg>
);

// Clock icon for sending status
const ClockIcon = ({ color = '#667781', size = 12 }) => (
  <svg viewBox="0 0 16 16" height={size} width={size} style={{ display: 'inline-block' }}>
    <path d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8z" fill={color}></path>
    <path d="M8 3.25a.75.75 0 0 1 .75.75v3.25H11a.75.75 0 0 1 0 1.5H7.25V4A.75.75 0 0 1 8 3.25z" fill={color}></path>
  </svg>
);

/**
 * Returns the appropriate tick icon based on message delivery status.
 * Only render for YOUR OWN (outgoing) messages.
 *
 *  'sending'   → clock icon            — waiting for server ack
 *  'sent'      → single grey  ✓        — message reached the server
 *  'delivered' → double grey  ✓✓       — recipient device received it
 *  'read'      → double BLUE  ✓✓       — recipient opened the conversation
 *  'failed'    → warning symbol         — delivery failed
 */
const renderMessageStatus = (status, colorTheme, chatTheme, windowWidth) => {
  const theme = (CHAT_THEMES[colorTheme] && CHAT_THEMES[colorTheme][chatTheme])
    ? CHAT_THEMES[colorTheme][chatTheme]
    : CHAT_THEMES.whatsapp.light;
  switch (status) {
    case 'read':
      return <DoubleTick color={theme.tickRead} size={windowWidth < 480 ? 16 : 18} />;
    case 'delivered':
      return <DoubleTick color={theme.tickDelivered} size={windowWidth < 480 ? 16 : 18} />;
    case 'sent':
      return <SingleTick color={theme.tickSent} size={windowWidth < 480 ? 14 : 16} />;
    case 'sending':
      return <ClockIcon color={theme.tickPending} size={windowWidth < 480 ? 12 : 14} />;
    case 'failed':
      return <span style={{ color: '#ff4d4f', fontSize: windowWidth < 480 ? 10 : 11 }}>⚠</span>;
    default:
      return <SingleTick color={theme.tickSent} size={windowWidth < 480 ? 14 : 16} />;
  }
};

// Get initials from name
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

// Get consistent avatar style based on phone number/name and current theme
const getAvatarStyle = (identifier, colorTheme = 'whatsapp') => {
  const avatars = CHAT_THEMES[colorTheme]?.avatars || CHAT_THEMES.whatsapp.avatars;
  if (!identifier || !avatars?.length) return avatars?.[0] || { bg: '#25D366', color: '#fff' };
  const hash = identifier.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return avatars[hash % avatars.length];
};

// ─── Date grouping utility ────────────────────────────────────────────────────
// Groups a flat messages array and injects { type:'date' } separators whenever
// the calendar day changes between consecutive messages.
// eslint-disable-next-line no-unused-vars
const groupMessagesByDate = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  const result = [];
  let lastDateKey = null;
  for (const msg of messages) {
    const d = msg.timestamp ? new Date(msg.timestamp) : null;
    // Build a YYYY-MM-DD key for pure calendar-day comparison
    const dateKey = d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : null;
    if (dateKey && dateKey !== lastDateKey) {
      result.push({ type: 'date', date: msg.timestamp });
      lastDateKey = dateKey;
    }
    result.push({ type: 'message', data: msg });
  }
  return result;
};

// ─── DateSeparator component ───────────────────────────────────────────────────
const DateSeparator = ({ date }) => {
  const label = (() => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const isToday =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    if (isToday) return 'Today';
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday =
      d.getFullYear() === yesterday.getFullYear() &&
      d.getMonth() === yesterday.getMonth() &&
      d.getDate() === yesterday.getDate();
    if (isYesterday) return 'Yesterday';
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
    });
  })();

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      margin: '10px 0 6px',
      padding: '0 4px',
      userSelect: 'none',
    }}>
      <div style={{ flex: 1, height: 1, background: 'rgba(0,0,0,0.10)' }} />
      <span style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: 'rgba(100,100,100,0.75)',
        background: 'transparent',
        padding: '2px 8px',
        borderRadius: 10,
        whiteSpace: 'nowrap',
      }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: 'rgba(0,0,0,0.10)' }} />
    </div>
  );
};

const getExternalStatus = (item = {}) => {
  const raw = item.external_status ?? item.externalStatus ?? (item.is_external_api || item.isExternalApi ? 1 : 0);
  return Number(raw) === 1 ? 1 : 0;
};

const normalizeRepliedToTemplate = (item = {}) => {
  const rawSource = item.repliedToTemplate || item.replied_to_template;
  const source = parseJsonIfPossible(rawSource) || rawSource;
  if (!source || typeof source !== 'object') return null;

  const templateId = source.templateId ?? source.template_id ?? source.templateid ?? null;
  const templateName = source.templateName ?? source.template_name ?? source.templatename ?? source.name ?? null;
  const campaignId = source.campaignId ?? source.campaign_id ?? source.campaignid ?? null;
  const campaignName = source.campaignName ?? source.campaign_name ?? source.campaignname ?? null;
  const messageWamid = source.messageWamid ?? source.message_wamid ?? source.whatsappMessageId ?? source.whatsapp_message_id ?? source.wamid ?? null;
  const sentAt = source.sentAt ?? source.sent_at ?? source.entrytime ?? null;
  const content = source.templateContent ?? source.template_content ?? source.content ?? source.message ?? null;
  const category = source.templateCategory ?? source.template_category ?? source.category ?? null;

  if (!templateId && !templateName && !campaignId && !campaignName && !messageWamid && !content) return null;

  return {
    templateId,
    templateName,
    campaignId,
    campaignName,
    messageWamid,
    sentAt,
    content,
    category,
    name: templateName,
  };
};

const getRepliedToTemplateLabel = (repliedToTemplate) => {
  if (!repliedToTemplate) return '';
  return repliedToTemplate.campaignName
    || repliedToTemplate.templateName
    || repliedToTemplate.name
    || repliedToTemplate.campaignId
    || repliedToTemplate.templateId
    || repliedToTemplate.messageWamid
    || '';
};

const sourceToConversationParams = (source) => {
  // The backend controller (callback.controller.js) reads `isExternalApi` from
  // req.query, NOT `externalStatus`. Pass the param the backend actually uses.
  if (source === 'internal') return { isExternalApi: false };
  if (source === 'external') return { isExternalApi: true };
  if (source === 'suppressed') return { suppressedAutomation: true };
  return {};
};

const CONVERSATION_PAGE_LIMIT = 50;
const CONTACT_SELECT_DEBOUNCE_MS = 350;
const MIN_CONVERSATION_FETCH_INTERVAL_MS = 600;

const getEmptyConversationPaging = () => ({
  total: 0,
  limit: CONVERSATION_PAGE_LIMIT,
  offset: 0,
  nextOffset: 0,
  hasMore: false,
});

const getNumberOrFallback = (value, fallback) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const getConversationTime = (timestamp) => {
  const time = new Date(timestamp || 0).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const normalizePhoneNumber = (value) => String(value || '').replace(/\D/g, '');

const isSamePhoneNumber = (a, b) => {
  const left = normalizePhoneNumber(a);
  const right = normalizePhoneNumber(b);
  if (!left || !right) return false;
  return left === right || left.endsWith(right) || right.endsWith(left);
};

const buildConversationPaging = (responseData, receivedCount, fallbackOffset = 0, fallbackLimit = CONVERSATION_PAGE_LIMIT) => {
  const pagination = responseData?.pagination || {};
  const limit = getNumberOrFallback(pagination.limit, fallbackLimit);
  const offset = getNumberOrFallback(pagination.offset, fallbackOffset);
  const total = getNumberOrFallback(pagination.total, 0);
  const nextOffset = offset + receivedCount;

  return {
    total,
    limit,
    offset,
    nextOffset,
    hasMore: total > 0 ? nextOffset < total : receivedCount >= limit,
  };
};

const transformConversationMessages = (messagesData = []) => {
  if (!Array.isArray(messagesData)) return [];

  const existingTemplateCampaignIds = new Set(
    messagesData
      .filter(msg => msg.msg_type === 'template' && msg.campaignid)
      .map(msg => String(msg.campaignid))
  );

  const syntheticTemplates = [];
  messagesData.forEach(msg => {
    const repliedToTemplate = normalizeRepliedToTemplate(msg);
    if (repliedToTemplate?.campaignId && repliedToTemplate.sentAt) {
      const campaignKey = String(repliedToTemplate.campaignId);
      if (!existingTemplateCampaignIds.has(campaignKey)) {
        existingTemplateCampaignIds.add(campaignKey);
        const externalStatus = getExternalStatus(msg);
        syntheticTemplates.push({
          id: `template-${repliedToTemplate.campaignId}`,
          text: repliedToTemplate.content || getRepliedToTemplateLabel(repliedToTemplate),
          fromUser: false,
          timestamp: repliedToTemplate.sentAt,
          status: 'delivered',
          isTemplate: true,
          templateName: repliedToTemplate.templateName || repliedToTemplate.campaignName,
          direction: 'outgoing',
          msg_type: 'template',
          isSynthetic: true,
          externalStatus,
          isExternalApi: externalStatus === 1,
        });
      }
    }
  });

  syntheticTemplates.forEach(template => {
    const campaignId = template.id.replace('template-', '');
    let earliestReplyTime = null;
    messagesData.forEach(msg => {
      const repliedToTemplate = normalizeRepliedToTemplate(msg);
      if (repliedToTemplate?.campaignId && String(repliedToTemplate.campaignId) === campaignId) {
        const replyTime = new Date(msg.entrytime || msg.sent_at);
        if (!earliestReplyTime || replyTime < earliestReplyTime) {
          earliestReplyTime = replyTime;
        }
      }
    });
    if (earliestReplyTime) {
      const templateTime = new Date(template.timestamp);
      if (templateTime >= earliestReplyTime) {
        template.timestamp = new Date(earliestReplyTime.getTime() - 1000).toISOString();
      }
    }
  });

  const transformedMessages = messagesData
    .filter(msg => {
      const msgType = getMessageType(msg);
      const msgContent = getDisplayMessageContent(msg, msgType);
      return (msgContent && String(msgContent).trim()) || msg.isMedia || isMediaType(msgType) || msg.mediaUrl;
    })
    .map((msg, index) => {
      const msgType = getMessageType(msg);
      const msgContent = getDisplayMessageContent(msg, msgType);
      const msgIsFlowResponse = isFlowResponseType(msgType);
      const msgIsMedia = !msgIsFlowResponse && (msg.isMedia || isMediaType(msgType));
      const msgMediaType = msg.mediaType || msgType;
      const externalStatus = getExternalStatus(msg);
      const repliedToTemplate = normalizeRepliedToTemplate(msg);

      return {
        id: msg.conversationid || msg.message_uuid || `${msg.entrytime || msg.sent_at || 'message'}-${index}`,
        text: msgIsMedia ? (msg.mediaPreview || getMediaPreview(msgMediaType)) : (msgIsFlowResponse ? getFlowResponseChatText() : msgContent),
        fromUser: msg.direction === 'incoming',
        timestamp: msg.entrytime || msg.sent_at,
        status: (msg.status && ['sent','delivered','read','failed'].includes(msg.status))
          ? msg.status
          : (Number(msg.isread) === 1
              ? 'read'
              : (msg.direction === 'outgoing' ? 'delivered' : 'sent')),
        waba_message_id: msg.waba_message_id,
        msg_type: msgType,
        direction: msg.direction,
        isTemplate: msgType === 'template',
        templateName: msgType === 'template' ? (msg.username === 'Campaign Message' ? 'Campaign' : msg.username) : null,
        isFlowResponse: msgIsFlowResponse,
        flowResponseText: msgIsFlowResponse ? msgContent : null,
        repliedToTemplate,
        mediaUrl: msg.mediaUrl || null,
        isMedia: msgIsMedia,
        mediaType: msgMediaType,
        mediaPreview: msg.mediaPreview || (msgIsMedia ? getMediaPreview(msgMediaType) : null),
        isImage: msgMediaType === 'image',
        isVideo: msgMediaType === 'video',
        isAudio: msgMediaType === 'audio',
        isDocument: msgMediaType === 'document',
        externalStatus,
        isExternalApi: externalStatus === 1,
        externalSource: msg.external_source || msg.externalSource || null,
        externalMessageId: msg.external_message_id || msg.externalMessageId || null,
        actor_type: msg.actor_type || msg.actorType || null,
        handled_by: msg.handled_by || msg.handledBy || null,
        ai_status: msg.ai_status ?? msg.aiStatus ?? 0,
        actor_name: msg.actor_name || msg.actorName || null,
        section_owner: msg.section_owner || msg.sectionOwner || null,
        section_id: msg.section_id || msg.sectionId || null,
        section_start: msg.section_start ?? msg.sectionStart ?? false,
        section_end: msg.section_end ?? msg.sectionEnd ?? false,
        section_label: msg.section_label || msg.sectionLabel || null,
      };
    });

  return [...transformedMessages, ...syntheticTemplates]
    .sort((a, b) => getConversationTime(a.timestamp) - getConversationTime(b.timestamp));
};

const mergeConversationMessages = (incomingMessages = [], existingMessages = []) => {
  const messageMap = new Map();
  [...incomingMessages, ...existingMessages].forEach((msg, index) => {
    const key = msg.id || `${msg.timestamp || 'no-time'}-${msg.direction || 'message'}-${msg.text || ''}-${index}`;
    messageMap.set(key, { ...(messageMap.get(key) || {}), ...msg });
  });

  return Array.from(messageMap.values())
    .sort((a, b) => getConversationTime(a.timestamp) - getConversationTime(b.timestamp));
};

const SourceBadge = ({ externalStatus, compact = false }) => {
  const isExternal = Number(externalStatus) === 1;
  if (!isExternal) return null;
  return (
    <span
      className={styles.sourceBadgeExternal}
      title="External API message"
      style={compact ? { fontSize: 9, padding: '1px 6px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', fontWeight: 800, whiteSpace: 'nowrap' } : undefined}
    >
      External API
    </span>
  );
};

// ─── AI / Human / Bot section divider ────────────────────────────────────────
const SECTION_META = {
  ai:     { label: 'AI Chatbot',    color: '#7c3aed', emoji: '🤖' },
  human:  { label: 'Human Agent',   color: '#2563eb', emoji: '👤' },
  bot:    { label: 'Bot Flow',      color: '#16a34a', emoji: '🔀' },
  system: { label: 'System',        color: '#6b7280', emoji: '⚙️' },
};

// Returns the section owner key for a message.
// Prefers backend's explicit section_owner field (covers user messages inside AI turns),
// then falls back to handled_by > actor_type client-side inference.
const getMessageHandler = (msg = {}) => {
  const so = msg.section_owner || msg.sectionOwner;
  if (so && so !== 'user') return so;
  const hb = msg.handled_by || msg.handledBy;
  if (hb && hb !== 'unassigned') return hb;
  const at = msg.actor_type || msg.actorType;
  if (at && at !== 'user') return at;
  return null;
};

// Groups a flat messages array, injecting date separators and section dividers.
// Uses backend's section_start flag when available, otherwise detects owner changes.
const groupMessagesWithSections = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  const result = [];
  let lastDateKey = null;
  let lastHandler = undefined;

  for (const msg of messages) {
    const d = msg.timestamp ? new Date(msg.timestamp) : null;
    const dateKey = d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : null;
    if (dateKey && dateKey !== lastDateKey) {
      result.push({ type: 'date', date: msg.timestamp });
      lastDateKey = dateKey;
    }

    // Use backend section_start flag if present; otherwise detect owner changes
    const hasBackendSectionStart = msg.section_start === true || msg.sectionStart === true;
    const owner = msg.section_owner || msg.sectionOwner || getMessageHandler(msg);
    const label = msg.section_label || msg.sectionLabel || null;

    if (owner && (hasBackendSectionStart || owner !== lastHandler)) {
      result.push({ type: 'section', owner, label });
      lastHandler = owner;
    }
    result.push({ type: 'message', data: msg });
  }
  return result;
};

// Visual divider rendered between sections — hidden, keeps grouping logic intact
const ChatSectionDivider = ({ owner, label, chatTheme }) => {
  return null;
};

const InboxPage = () => {
  const [loading, setLoading] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [conversationPaging, setConversationPaging] = useState(getEmptyConversationPaging);
  const [reply, setReply] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  // Per-user chat message filter (right-side panel): 'all' | 'internal' | 'external' | 'ai'
  const [chatFilter, setChatFilter] = useState('all');
  const [labelFilter, setLabelFilter] = useState([]);

  // Label contact from inbox (for external/unknown contacts)
  const [labelContactModalOpen, setLabelContactModalOpen] = useState(false);
  const [labelContactLoading, setLabelContactLoading] = useState(false);
  const [labelContactBooks, setLabelContactBooks] = useState([]);
  const [labelContactBooksLoading, setLabelContactBooksLoading] = useState(false);
  const [labelContactSelectedBook, setLabelContactSelectedBook] = useState(null);
  const [labelContactForm] = Form.useForm();

  // Predefined label definitions (same as ContactsPage)
  const predefinedLabels = [
    { value: 'favourite', label: 'Favourite', icon: <HeartOutlined /> },
    { value: 'close', label: 'Close Friend', icon: <StarOutlined /> },
    { value: 'family', label: 'Family', icon: <HomeOutlined /> },
    { value: 'business', label: 'Business', icon: <TagOutlined /> },
    { value: 'vip', label: 'VIP Customer', icon: <StarOutlined /> },
  ];
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(360);
  const [isResizing, setIsResizing] = useState(false);
  // Get theme settings from context (synced with API)
  const { 
    colorTheme, 
    displayMode: chatTheme, 
    updateColorTheme, 
    updateDisplayMode
  } = useUISettings();
  const { openMobileDrawer } = useSidebar();
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [showSidebar, setShowSidebar] = useState(true);
  const [notificationSoundEnabled, setNotificationSoundEnabled] = useState(true);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [showMediaPreview, setShowMediaPreview] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [mediaCaption, setMediaCaption] = useState('');
  // State for viewing media in chat (lightbox)
  const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
  const [mediaViewerUrl, setMediaViewerUrl] = useState('');
  const [mediaViewerFallbackUrl, setMediaViewerFallbackUrl] = useState(''); // Fallback URL (local preview)
  const [mediaViewerType, setMediaViewerType] = useState('image'); // 'image', 'video'
  // Zoom and pan state
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePan, setImagePan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const { userCtx } = useUserContext();
  const inboxWriteAllowed = !userCtx || !userCtx.pages || canWrite(userCtx, 'inbox');
  const {
    isConnected: wsConnected,
    messages: wsMessages,
    statusUpdates: wsStatusUpdates,
    contactUpdates: wsContactUpdates,
    subscribeToConversation,
    unsubscribeFromConversation,
    markAsRead
  } = useWebSocketClient();
  const messagesEndRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const messagesAreaRef = React.useRef(null);
  const initialWidthRef = React.useRef(0);
  const initialMouseXRef = React.useRef(0);
  const emojiPickerRef = React.useRef(null);
  const contactSelectDebounceRef = React.useRef(null);
  const loadingOlderMessagesRef = React.useRef(false);
  const lastConversationFetchAtRef = React.useRef(0);
  const preserveScrollOnConversationChangeRef = React.useRef(false);
  // Ref to track selected contact's phone number synchronously (avoids stale state in WebSocket handlers)
  const selectedPhoneNumberRef = React.useRef(null);
  // AbortController for the in-flight /callback/conversations request.
  // Cancels the previous request whenever a new contact is clicked (prevents stale responses
  // from overwriting a newer selection and eliminates redundant backend calls).
  const fetchConversationAbortRef = React.useRef(null);
  // Tracks the phone number currently being fetched so rapid duplicate clicks are ignored.
  const fetchConversationPhoneRef = React.useRef(null);
  
  // Ref to track contacts that were recently marked as read (prevents WebSocket from overriding)
  // Structure: Map<phoneNumber, timestamp>
  const readContactsRef = React.useRef(new Map());
  const READ_PROTECTION_DURATION = 60000; // 60 seconds protection window
  
  // Helper function to check if a contact was recently marked as read
  const isRecentlyRead = (phoneNumber) => {
    const normalizedPhone = normalizePhoneNumber(phoneNumber);
    const readTime = readContactsRef.current.get(normalizedPhone);
    if (!readTime) return false;
    const isRecent = Date.now() - readTime < READ_PROTECTION_DURATION;
    if (!isRecent) {
      // Clean up expired entry
      readContactsRef.current.delete(normalizedPhone);
    }
    return isRecent;
  };
  
  // Helper function to mark a contact as read
  const markContactAsRead = (phoneNumber) => {
    const normalizedPhone = normalizePhoneNumber(phoneNumber);
    readContactsRef.current.set(normalizedPhone, Date.now());
    // Clean up old entries periodically
    readContactsRef.current.forEach((time, phone) => {
      if (Date.now() - time > READ_PROTECTION_DURATION * 2) {
        readContactsRef.current.delete(phone);
      }
    });
  };

  // Keep the ref in sync with selectedContact state
  useEffect(() => {
    selectedPhoneNumberRef.current = selectedContact?.phoneNumber || null;
  }, [selectedContact]);

  // Defensive: whenever a contact is opened, ensure its unread badge is 0
  useEffect(() => {
    if (!selectedContact?.phoneNumber) return;
    setContacts(prev => prev.map(c =>
      isSamePhoneNumber(c.phoneNumber, selectedContact.phoneNumber) ? { ...c, unreadCount: 0 } : c
    ));
  }, [selectedContact]);
  
  // Sync total unread count to DashboardLayout via custom event
  useEffect(() => {
    if (contacts && Array.isArray(contacts)) {
      const totalUnread = contacts.reduce((sum, contact) => sum + (contact.unreadCount || 0), 0);
      // Dispatch custom event to sync with DashboardLayout sidebar badge
      window.dispatchEvent(new CustomEvent('inboxUnreadCountSync', { 
        detail: { count: totalUnread } 
      }));
      console.log('📢 Syncing total unread count to sidebar:', totalUnread);
    }
  }, [contacts]);
  
  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    
    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);
  
  // Clear selected contact when component unmounts
  useEffect(() => {
    return () => {
      if (contactSelectDebounceRef.current) {
        clearTimeout(contactSelectDebounceRef.current);
      }
      if (fetchConversationAbortRef.current) {
        fetchConversationAbortRef.current.abort();
      }
      setSelectedContact(null);
    };
  }, []);
  
  // Handle emoji selection
  const handleEmojiSelect = (emoji) => {
    setReply(prev => prev + emoji);
    // Keep emoji picker open for multiple selections
  };

  // Handle sidebar resize
  const handleMouseDown = (e) => {
    setIsResizing(true);
    initialMouseXRef.current = e.clientX;
    initialWidthRef.current = sidebarWidth;
  };

  // Subscribe/unsubscribe to selected contact via shared WebSocket
  useEffect(() => {
    if (!selectedContact?.phoneNumber) return undefined;
    subscribeToConversation(selectedContact.phoneNumber);
    return () => unsubscribeFromConversation(selectedContact.phoneNumber);
  }, [selectedContact, subscribeToConversation, unsubscribeFromConversation]);

  // Handle incoming message
  const handleIncomingMessage = React.useCallback((data) => {
    const { phoneNumber, timestamp, messageId, profileName,
            isMedia: dataIsMedia, mediaType: dataMsgType, mediaUrl: dataMediaUrl, mediaPreview: dataMediaPreview } = data;
    const msg_type = getMessageType(data);
    const msgContent = getDisplayMessageContent(data, msg_type);
    const incomingIsFlowResponse = isFlowResponseType(msg_type);
    
    console.log('📨 INCOMING MESSAGE:', { phoneNumber, msgContent, selectedContact: selectedContact?.phoneNumber });
    
    // Determine if this is a media message
    const incomingIsMedia = !incomingIsFlowResponse && (dataIsMedia || isMediaType(msg_type));
    const incomingMediaType = dataMsgType || msg_type;
    const incomingMediaPreview = dataMediaPreview || (incomingIsMedia ? getMediaPreview(incomingMediaType) : null);
    const bubbleMessage = incomingIsMedia ? incomingMediaPreview : (incomingIsFlowResponse ? getFlowResponseChatText() : msgContent);
    const displayMessage = getLastMessageDisplay(msgContent, msg_type, incomingIsMedia, incomingMediaPreview);
    const repliedToTemplate = normalizeRepliedToTemplate(data);
    
    const newMsg = {
      id: messageId || Date.now(),
      text: bubbleMessage,
      fromUser: true, // Received message
      timestamp: timestamp || new Date().toISOString(),
      status: 'delivered',
      // NEW: Media fields
      mediaUrl: dataMediaUrl || null,
      isMedia: incomingIsMedia,
      mediaType: incomingMediaType,
      mediaPreview: incomingMediaPreview,
      isImage: incomingMediaType === 'image',
      isVideo: incomingMediaType === 'video',
      isAudio: incomingMediaType === 'audio',
      isDocument: incomingMediaType === 'document',
      msg_type: msg_type,
      isFlowResponse: incomingIsFlowResponse,
      flowResponseText: incomingIsFlowResponse ? msgContent : null,
      repliedToTemplate
    };

    // Update conversation if it's the currently selected contact
    if (isSamePhoneNumber(selectedContact?.phoneNumber, phoneNumber)) {
      setConversation(prev => [...prev, newMsg]);
    }

    // Always update contact's last message, only increment unread count if not selected
    setContacts(prevContacts => {
      const existingContact = prevContacts.find(c => isSamePhoneNumber(c.phoneNumber, phoneNumber));
      if (existingContact) {
        return prevContacts.map(contact => {
          if (isSamePhoneNumber(contact.phoneNumber, phoneNumber)) {
            // Use ref for synchronous check to avoid stale state
            const isCurrentlySelected = isSamePhoneNumber(selectedPhoneNumberRef.current, phoneNumber);
            // Check if this contact was recently marked as read
            const wasRecentlyRead = isRecentlyRead(phoneNumber);
            console.log('📨 handleIncomingMessage - contact:', phoneNumber, 'selected:', isCurrentlySelected, 'recentlyRead:', wasRecentlyRead, 'current count:', contact.unreadCount);
            
            // Determine new unread count:
            // - If currently selected OR recently read: keep at 0
            // - Otherwise: increment
            let newUnreadCount;
            if (isCurrentlySelected || wasRecentlyRead) {
              newUnreadCount = 0;
            } else {
              newUnreadCount = (contact.unreadCount || 0) + 1;
            }
            
            return {
              ...contact,
              lastMessage: displayMessage,
              lastMessageTime: timestamp || new Date().toISOString(),
              unreadCount: newUnreadCount,
              // Never carry forward a previous outgoing-template reference onto an incoming message.
              // handleIncomingMessage is always fromUser:true (flag=0), so the fallback is dropped.
              repliedToTemplate: repliedToTemplate || null
            };
          }
          return contact;
        });
      } else {
        // New contact - add to top of list
        const isCurrentlySelected = isSamePhoneNumber(selectedPhoneNumberRef.current, phoneNumber);
        const newContact = {
          phoneNumber,
          name: profileName || phoneNumber,
          lastMessage: displayMessage,
          lastMessageTime: timestamp || new Date().toISOString(),
          unreadCount: isCurrentlySelected ? 0 : 1,
          status: 'offline',
          avatar: null,
          repliedToTemplate
        };
        return [newContact, ...prevContacts];
      }
    });

    // Show notification if not the active contact
    if (!isSamePhoneNumber(selectedContact?.phoneNumber, phoneNumber)) {
      // message.info(`New message from ${profileName || phoneNumber}`);
    }
  }, [selectedContact]);

  // Handle message status updates (sent, delivered, read)
  const handleMessageStatusUpdate = React.useCallback((data) => {
    const { messageId, status, phoneNumber } = data;
    
    if (isSamePhoneNumber(selectedContact?.phoneNumber, phoneNumber)) {
      setConversation(prev => 
        prev.map(msg => 
          msg.id === messageId ? { ...msg, status } : msg
        )
      );
    }
  }, [selectedContact]);

  // Handle user status updates (online/offline)
  const handleUserStatusUpdate = React.useCallback((data) => {
    const { phoneNumber, status } = data;
    
    setContacts(prevContacts => 
      prevContacts.map(contact => 
        isSamePhoneNumber(contact.phoneNumber, phoneNumber)
          ? { ...contact, status }
          : contact
      )
    );
    
    if (isSamePhoneNumber(selectedContact?.phoneNumber, phoneNumber)) {
      setSelectedContact(prev => ({ ...prev, status }));
    }
  }, [selectedContact]);

  // Handle conversation updates - triggers refetch when needed
  const [shouldRefetchConversation, setShouldRefetchConversation] = React.useState(false);
  
  const handleConversationUpdate = React.useCallback((data) => {
    const { phoneNumber } = data;
    
    // Mark for refetch if it's the currently selected contact
    if (isSamePhoneNumber(selectedContact?.phoneNumber, phoneNumber)) {
      setShouldRefetchConversation(true);
    }
  }, [selectedContact]);

  // Keep legacy handlers referenced for compatibility with older websocket event wiring.
  void handleMessageStatusUpdate;
  void handleUserStatusUpdate;
  void handleConversationUpdate;

  // Effect to handle conversation refetch
  useEffect(() => {
    if (shouldRefetchConversation && selectedContact) {
      setShouldRefetchConversation(false);
      // Re-fetch conversation
      const refetch = async () => {
        try {
          const response = await axiosConfig.post('/callback/conversations', {
            campaignId: '',
            phoneNumber: selectedContact.phoneNumber,
            limit: CONVERSATION_PAGE_LIMIT,
            offset: 0
          });
          if (response.data?.success && Array.isArray(response.data?.data)) {
            const messagesData = response.data.data;
            setConversation(transformConversationMessages(messagesData));
            setConversationPaging(buildConversationPaging(response.data, messagesData.length, 0, CONVERSATION_PAGE_LIMIT));
          }
        } catch (err) {
          console.error('Failed to refetch conversation:', err);
        }
      };
      refetch();
    }
  }, [shouldRefetchConversation, selectedContact]);

  // Handle incoming WebSocket messages from shared service
  useEffect(() => {
    if (!wsMessages.length) return;
    const latest = wsMessages[wsMessages.length - 1];

    if (latest.type === 'INCOMING') {
      const latestMsgType = getMessageType(latest);
      const latestContent = getDisplayMessageContent(latest, latestMsgType);
      handleIncomingMessage({
        phoneNumber: latest.phoneNumber,
        message: latestContent,
        content: latestContent,
        messageContent: latest.messageContent,
        timestamp: latest.timestamp,
        messageId: latest.messageId,
        conversationId: latest.conversationId,
        profileName: latest.profileName,
        // Pass media fields
        msg_type: latestMsgType,
        message_type: latest.message_type,
        messageType: latest.messageType,
        mediaUrl: latest.mediaUrl,
        isMedia: latest.isMedia,
        mediaType: latest.mediaType || latest.msg_type || latest.message_type || latest.messageType,
        mediaPreview: latest.mediaPreview,
        repliedToTemplate: latest.repliedToTemplate,
        replied_to_template: latest.replied_to_template
      });
    }

    // Handle CONVERSATION_MESSAGE_UPDATE - updates active chat panel
    if (latest.type === 'CONVERSATION_MESSAGE_UPDATE') {
      console.log('🔔 CONVERSATION MESSAGE UPDATE:', latest);

      const messagePhoneNumber = latest.phoneNumber || latest.mobileno;
      // Use ref for synchronous check to avoid stale state issues
      const isCurrentlySelected = isSamePhoneNumber(selectedPhoneNumberRef.current, messagePhoneNumber);
      const latestMsgType = getMessageType(latest);
      const latestContent = getDisplayMessageContent(latest, latestMsgType);
      const latestIsFlowResponse = isFlowResponseType(latestMsgType);
      
      // Determine if this is a media message from WebSocket
      const wsIsMedia = !latestIsFlowResponse && (latest.isMedia || isMediaType(latestMsgType));
      const wsMediaType = latest.mediaType || latestMsgType;
      const wsMediaPreview = latest.mediaPreview || (wsIsMedia ? getMediaPreview(wsMediaType) : null);
      // For lastMessage display: use mediaPreview for media, content for text
      const displayLastMessage = getLastMessageDisplay(latestContent, latestMsgType, wsIsMedia, wsMediaPreview);
      const repliedToTemplate = normalizeRepliedToTemplate(latest);

      // Always update the contact's last message and time
      setContacts(prevContacts => {
        const existingContact = prevContacts.find(c => isSamePhoneNumber(c.phoneNumber, messagePhoneNumber));
        // Check if this contact was recently marked as read
        const wasRecentlyRead = isRecentlyRead(messagePhoneNumber);
        
        if (existingContact) {
          return prevContacts.map(contact => {
            if (isSamePhoneNumber(contact.phoneNumber, messagePhoneNumber)) {
              console.log('📨 CONVERSATION_MESSAGE_UPDATE - contact:', messagePhoneNumber, 'selected:', isCurrentlySelected, 'recentlyRead:', wasRecentlyRead, 'current count:', contact.unreadCount);
              
              // Determine new unread count:
              // - If currently selected OR recently read: keep at 0
              // - Otherwise: increment
              let newUnreadCount;
              if (isCurrentlySelected || wasRecentlyRead) {
                newUnreadCount = 0;
              } else {
                // Only increment for incoming messages (flag=0). Outgoing (flag=1) never adds unread.
                newUnreadCount = (latest.flag === 0) ? (contact.unreadCount || 0) + 1 : (contact.unreadCount || 0);
              }
              
              return {
                ...contact,
                lastMessage: displayLastMessage,
                lastMessageTime: latest.timestamp || new Date().toISOString(),
                unreadCount: newUnreadCount,
                // Only carry forward the previous repliedToTemplate for outgoing messages (flag=1).
                // Incoming messages (flag=0) should only show what the backend explicitly sends.
                repliedToTemplate: latest.flag === 0
                  ? (repliedToTemplate || null)
                  : (repliedToTemplate || contact.repliedToTemplate || null)
              };
            }
            return contact;
          });
        } else {
          // New contact - add to top of list
          const newContact = {
            phoneNumber: messagePhoneNumber,
            name: latest.username || messagePhoneNumber,
            lastMessage: displayLastMessage,
            lastMessageTime: latest.timestamp || new Date().toISOString(),
            unreadCount: isCurrentlySelected ? 0 : 1,
            status: 'offline',
            avatar: null,
            repliedToTemplate
          };
          return [newContact, ...prevContacts];
        }
      });

      // Only add to conversation if it's the currently selected contact
      if (isCurrentlySelected) {
        const newMsg = {
          id: latest.conversationId || Date.now(),
          text: wsIsMedia ? wsMediaPreview : (latestIsFlowResponse ? getFlowResponseChatText() : latestContent),
          fromUser: latest.flag === 0, // 0 = incoming, 1 = outgoing
          timestamp: latest.timestamp,
          status: latest.flag === 0 ? 'delivered' : 'sent',
          // NEW: Media fields from signed URL architecture (WebSocket)
          mediaUrl: latest.mediaUrl || null,
          isMedia: wsIsMedia,
          mediaType: wsMediaType,
          mediaPreview: wsMediaPreview,
          isImage: wsMediaType === 'image',
          isVideo: wsMediaType === 'video',
          isAudio: wsMediaType === 'audio',
          isDocument: wsMediaType === 'document',
          msg_type: latestMsgType,
          isFlowResponse: latestIsFlowResponse,
          flowResponseText: latestIsFlowResponse ? latestContent : null,
          externalStatus: latest.external_status != null ? Number(latest.external_status) : (latest.externalStatus != null ? Number(latest.externalStatus) : 0),
          isExternalApi: Number(latest.external_status || latest.externalStatus || 0) === 1,
          actor_type: latest.actor_type || latest.actorType || null,
          handled_by: latest.handled_by || latest.handledBy || null,
          ai_status: latest.ai_status ?? latest.aiStatus ?? 0,
          repliedToTemplate,
        };

        // Check if message already exists — if it does, update its status instead of skipping
        setConversation(prev => {
          const existingIdx = prev.findIndex(msg =>
            msg.id === newMsg.id ||
            (msg.text === newMsg.text && msg.timestamp === newMsg.timestamp)
          );
          if (existingIdx >= 0) {
            // Message exists — update its status if the WS event carries a newer status
            const existing = prev[existingIdx];
            const statusRank = { sending: 0, sent: 1, delivered: 2, read: 3 };
            const incomingRank = statusRank[newMsg.status] ?? -1;
            const currentRank = statusRank[existing.status] ?? -1;
            if (incomingRank > currentRank) {
              const updated = [...prev];
              updated[existingIdx] = { ...existing, status: newMsg.status };
              return updated;
            }
            return prev;
          }
          return [...prev, newMsg];
        });
      }
    }    if (latest.type === 'REPLY_SENT') {
      // Backend now sends wamid — store it on the optimistic message so that
      // future MESSAGE_STATUS_UPDATE events (which arrive keyed by wamid) will
      // find their target message and update the tick.
      setConversation(prev => prev.map(msg => {
        // Match by conversationId or by the local messageId we assigned
        if (msg.id === latest.messageId || msg.id === latest.conversationId) {
          return {
            ...msg,
            status: 'sent',
            wamid: latest.wamid || msg.wamid,
            waba_message_id: latest.wamid || msg.waba_message_id,
          };
        }
        return msg;
      }));
    }
  }, [wsMessages, handleIncomingMessage, selectedContact]);

  // Handle delivery/read status updates (including bulk MESSAGES_READ → blue tick)
  useEffect(() => {
    if (!wsStatusUpdates.length) return;
    const latest = wsStatusUpdates[wsStatusUpdates.length - 1];

    // ── Bulk read: recipient opened the conversation → flip all outgoing msgs to 'read'
    if (latest.type === 'MESSAGES_READ') {
      const { phoneNumber } = latest;
      if (isSamePhoneNumber(selectedContact?.phoneNumber, phoneNumber)) {
        setConversation(prev =>
          prev.map(msg => ({
            ...msg,
            // Only flip OUR (outgoing) messages — leave inbound messages unchanged
            status: !msg.fromUser ? 'read' : msg.status,
          }))
        );
        console.log('🔵 MESSAGES_READ: all outgoing msgs set to read for', phoneNumber);
      }
      return;
    }

    // ── Individual per-message status update (sent → delivered → read)
    // Backend sends wamid as the unique key. Match against msg.wamid first,
    // then fall back to waba_message_id and id.
    const { whatsappMessageId, wamid, status, phoneNumber } = latest;
    const matchId = wamid || whatsappMessageId;

    if (isSamePhoneNumber(selectedContact?.phoneNumber, phoneNumber) && matchId) {
      setConversation(prev => {
        // Check if any message directly matches by wamid / waba_message_id / id
        const hasDirectMatch = prev.some(
          msg => msg.wamid === matchId || msg.waba_message_id === matchId || msg.id === matchId
        );

        if (hasDirectMatch) {
          // Update the specific message that matches (only upgrade — never downgrade)
          const STATUS_RANK = { sending: 0, sent: 1, delivered: 2, read: 3, failed: -1 };
          return prev.map(msg => {
            if (msg.wamid === matchId || msg.waba_message_id === matchId || msg.id === matchId) {
              if ((STATUS_RANK[status] ?? -1) > (STATUS_RANK[msg.status] ?? -1)) {
                return { ...msg, status };
              }
            }
            return msg;
          });
        }

        // If status is 'read' and there's no direct match, the backend may be sending
        // a read receipt for the conversation — flip all outgoing msgs to 'read'
        if (status === 'read') {
          return prev.map(msg => ({
            ...msg,
            status: !msg.fromUser ? 'read' : msg.status,
          }));
        }

        // If status is 'delivered' and there's no direct match, upgrade all 'sent' outgoing to 'delivered'
        if (status === 'delivered') {
          return prev.map(msg => ({
            ...msg,
            status: (!msg.fromUser && msg.status === 'sent') ? 'delivered' : msg.status,
          }));
        }

        return prev;
      });
    }
  }, [wsStatusUpdates, selectedContact]);

  // Handle CONVERSATION_USERS_UPDATE - updates contact list sidebar
  useEffect(() => {
    if (!wsContactUpdates.length) return;
    const latest = wsContactUpdates[wsContactUpdates.length - 1];
    const latestExternalStatus = getExternalStatus(latest);

    if (
      (sourceFilter === 'internal' && latestExternalStatus === 1) ||
      (sourceFilter === 'external' && latestExternalStatus === 0)
    ) {
      return;
    }
    
    console.log('🔔 CONTACT UPDATE:', latest);
    
    // Determine media preview for last message display
    const contactMsgType = getMessageType(latest);
    const contactMessageText = getDisplayLastMessageContent(latest, contactMsgType);
    const contactIsMedia = !isFlowResponseType(contactMsgType) && (latest.last_is_media || isMediaType(contactMsgType));
    const contactMediaPreview = latest.last_media_preview || (contactIsMedia ? getMediaPreview(contactMsgType) : null);
    const displayLastMessage = getLastMessageDisplay(contactMessageText, contactMsgType, contactIsMedia, contactMediaPreview);
    const repliedToTemplate = normalizeRepliedToTemplate(latest);
    
    setContacts(prevContacts => {
      const existingIndex = prevContacts.findIndex(
        c => isSamePhoneNumber(c.phoneNumber, latest.mobileno)
      );
      
      // Use ref for synchronous check
      const isCurrentlySelected = isSamePhoneNumber(selectedPhoneNumberRef.current, latest.mobileno);
      // Check if this contact was recently marked as read (prevents backend from overriding)
      const wasRecentlyRead = isRecentlyRead(latest.mobileno);
      
      if (existingIndex >= 0) {
        // Update existing contact and move to top
        const updated = [...prevContacts];
        const existingContact = updated[existingIndex];
        
        // Calculate unread count:
        // - If currently selected OR recently read: always 0 (PROTECTED)
        // - Otherwise: ALWAYS increment by 1 (this represents a NEW message arriving)
        // NOTE: Don't trust backend's unreadcount here - it can be stale/delayed
        let newUnreadCount;
        if (isCurrentlySelected || wasRecentlyRead) {
          // PROTECTED: User just read this chat, ignore backend's stale unreadcount
          newUnreadCount = 0;
          console.log('🛡️ PROTECTED: Keeping unreadCount=0 for', latest.mobileno, '(selected:', isCurrentlySelected, 'recentlyRead:', wasRecentlyRead, ')');
        } else {
          // NOT protected: Only increment for incoming messages (flag=0). Outgoing (flag=1) never adds unread.
          newUnreadCount = (latest.flag === 0) ? (existingContact.unreadCount || 0) + 1 : (existingContact.unreadCount || 0);
          console.log('📬 NEW MESSAGE: flag=', latest.flag, 'unreadCount for', latest.mobileno, 'from', existingContact.unreadCount, 'to', newUnreadCount);
        }
        
        const contact = {
          ...existingContact,
          name: latest.username || existingContact.name,
          lastMessage: displayLastMessage,
          lastMessageTime: latest.lastmessagetime,
          unreadCount: newUnreadCount,
          campaignId: latest.campaignid,
          externalStatus: latestExternalStatus,
          isExternalApi: latestExternalStatus === 1,
          externalSource: latest.external_source || latest.externalSource || existingContact.externalSource || null,
          externalMessageId: latest.external_message_id || latest.externalMessageId || existingContact.externalMessageId || null,
          // Only carry forward the previous repliedToTemplate for outgoing messages (flag=1).
          // Incoming messages (flag=0) should only show what the backend explicitly sends.
          repliedToTemplate: latest.flag === 0
            ? (repliedToTemplate || null)
            : (repliedToTemplate || existingContact.repliedToTemplate || null),
        };
        updated.splice(existingIndex, 1);
        return [contact, ...updated];
      } else {
        // Add new contact at top
        const newContact = {
          phoneNumber: latest.mobileno,
          name: latest.username || latest.mobileno,
          lastMessage: displayLastMessage,
          lastMessageTime: latest.lastmessagetime,
          unreadCount: (isCurrentlySelected || wasRecentlyRead) ? 0 : (latest.flag === 0 ? 1 : 0),
          status: 'offline',
          avatar: null,
          campaignId: latest.campaignid,
          externalStatus: latestExternalStatus,
          isExternalApi: latestExternalStatus === 1,
          externalSource: latest.external_source || latest.externalSource || null,
          externalMessageId: latest.external_message_id || latest.externalMessageId || null,
          repliedToTemplate,
        };
        return [newContact, ...prevContacts];
      }
    });
  }, [wsContactUpdates, selectedContact, sourceFilter]);

  // Track previous width to detect resize transitions
  const prevWidthRef = useRef(typeof window !== 'undefined' ? window.innerWidth : 1024);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const prevWidth = prevWidthRef.current;
      
      setWindowWidth(newWidth);
      
      // Resizing from desktop to mobile
      if (prevWidth >= 768 && newWidth < 768) {
        // If a contact is selected, show the chat panel
        // If no contact, show the sidebar (contacts list)
        setShowSidebar(!selectedContact);
      }
      // Resizing from mobile to desktop - always show sidebar
      else if (prevWidth < 768 && newWidth >= 768) {
        setShowSidebar(true);
      }
      
      prevWidthRef.current = newWidth;
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [selectedContact]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const diff = e.clientX - initialMouseXRef.current;
      const newWidth = initialWidthRef.current + diff;
      
      if (newWidth > 250 && newWidth < 600) {
        setSidebarWidth(newWidth);
      }
    }; 

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, sidebarWidth]);

  // Fetch users/contacts from API - get all conversations and group by contact
  useEffect(() => {
    const parseLabel = (raw) => {
      if (!raw) return [];
      if (Array.isArray(raw)) return raw.filter(Boolean);
      if (typeof raw === 'string') {
        const s = raw.trim();
        if (s.startsWith('[') && s.endsWith(']')) {
          try { return JSON.parse(s).filter(Boolean); } catch (e) {}
        }
        return s.split(',').map(l => l.trim()).filter(Boolean);
      }
      return [];
    };

    const fetchUsers = async () => {
      try {
        setContactsLoading(true);
        console.log('🔍 Fetching conversations...', { sourceFilter });

        // Fetch all conversations from the normal inbox/conversation flow.
        // External API conversations are now a source filter, not a separate inbox.
        const response = await axiosConfig.get('/callback/conversations/users', {
          params: {
            limit: 100,
            offset: 0,
            ...sourceToConversationParams(sourceFilter),
          }
        });
        
        console.log('📥 API Response:', response.data);
        const usersData = response.data?.data?.users || response.data?.data || [];

        // Debug: show raw external_status fields from first 3 contacts
        if (Array.isArray(usersData) && usersData.length > 0) {
          console.log('🔬 Raw external_status sample:', usersData.slice(0, 3).map(u => ({
            mobileno: u.mobileno,
            external_status: u.external_status,
            externalStatus: u.externalStatus,
            is_external_api: u.is_external_api,
            isExternalApi: u.isExternalApi,
          })));
        }
        
        if (response.data?.success && Array.isArray(usersData) && usersData.length > 0) {
          console.log('✅ Got users:', usersData.length);

          // Transform API response to contact objects
          const transformedContacts = usersData
            .filter(user => user.mobileno && user.mobileno !== '0')
            .map(user => {
              const lastMsgType = getMessageType(user) || user.last_msg_type;
              const rawLastMessage = getDisplayLastMessageContent(user, lastMsgType) || user.last_message || '';
              const lastIsMedia = !isFlowResponseType(lastMsgType) && (user.last_is_media || isMediaType(lastMsgType));
              const lastMediaPreview = user.last_media_preview;
              const lastMessage = getLastMessageDisplay(rawLastMessage, lastMsgType, lastIsMedia, lastMediaPreview);
              const repliedToTemplate = normalizeRepliedToTemplate(user);
              
              let contactName = 'Unknown';
              if (user.username) contactName = user.username;
              else if (user.mobileno) contactName = user.mobileno;

              // The backend controller strips external_status from formattedUsers.
              // When a source filter is active, all returned contacts are already
              // that type, so we can safely infer externalStatus from sourceFilter.
              const inferredExternalStatus =
                sourceFilter === 'external' ? 1
                  : sourceFilter === 'internal' ? 0
                  : getExternalStatus(user);

              return {
                phoneNumber: user.mobileno,
                name: contactName,
                lastMessage: lastMessage,
                lastMessageTime: user.lastmessagetime,
                unreadCount: user.unread_count || 0,
                status: 'offline',
                avatar: null,
                unreadMessages: user.unread_messages || [],
                externalStatus: inferredExternalStatus,
                isExternalApi: inferredExternalStatus === 1,
                externalSource: user.external_source || user.externalSource || null,
                externalMessageId: user.external_message_id || user.externalMessageId || null,
                repliedToTemplate,
                suppressedAutomation: user.suppressed_automation === true,
                // Try labels from API first; addressbook enrichment fills the rest
                labels: parseLabel(user.label)
              };
            })
            .sort((a, b) => new Date(b.lastMessageTime) - new Date(a.lastMessageTime));
          
          const extCount = transformedContacts.filter(c => c.externalStatus === 1).length;
          const intCount = transformedContacts.filter(c => c.externalStatus === 0).length;
          console.log('✅ Transformed contacts:', transformedContacts.length, `| internal: ${intCount} | external: ${extCount}`);
          setContacts(transformedContacts);

          // ── Background label enrichment from addressbooks ─────────────────────
          // Runs silently after contacts are already visible.
          // This is the reliable fallback when the backend SQL subquery is
          // unavailable (e.g. server not yet restarted after backend change).
          (async () => {
            try {
              const booksRes = await axiosConfig.get('/addressbook/details');
              const books = booksRes.data?.entity || [];
              if (!books.length) return;

              const phoneLabelMap = new Map(); // key: digits-only phone → { labels, userid, bookid }
              await Promise.all(books.map(async (book) => {
                try {
                  const cRes = await axiosConfig.post('/addressbook/users', {
                    bookid: book.bookid, limit: 10000, offset: 0,
                  });
                  const entity = cRes.data?.entity;
                  const cData = entity?.users || (Array.isArray(entity) ? entity : []);
                  cData.forEach((c) => {
                    if (!c.mobileno) return;
                    const lbls = parseLabel(c.label || c.labels || c.userlabels || c.userdata_labels || '');
                    if (!lbls.length) return;
                    // Store by digits-only so country code differences don't matter
                    const key = String(c.mobileno).replace(/\D/g, '');
                    const prev = phoneLabelMap.get(key);
                    const mergedLabels = Array.from(new Set([...(prev?.labels || []), ...lbls]));
                    phoneLabelMap.set(key, { labels: mergedLabels, userid: c.userid, bookid: book.bookid });
                  });
                } catch { /* skip individual book errors silently */ }
              }));

              if (!phoneLabelMap.size) return;

              setContacts(prev => prev.map(contact => {
                const cleanPhone = String(contact.phoneNumber).replace(/\D/g, '');
                // 1. exact digits match
                let entry = phoneLabelMap.get(cleanPhone);
                // 2. suffix match: handles "919876543210" vs "9876543210"
                if (!entry) {
                  phoneLabelMap.forEach((val, key) => {
                    if (!entry) {
                      if (cleanPhone.endsWith(key) || key.endsWith(cleanPhone)) entry = val;
                    }
                  });
                }
                if (!entry || !entry.labels.length) return contact;
                return { ...contact, labels: entry.labels, addressbookUserId: entry.userid, addressbookBookId: entry.bookid };
              }));
              console.log('✅ Labels enriched from addressbooks, map size:', phoneLabelMap.size);
            } catch (e) {
              console.warn('⚠️ Could not enrich labels from addressbook:', e);
            }
          })();
          // ─────────────────────────────────────────────────────────────────────

        } else {
          console.warn('⚠️ No data in response, using demo data');
          setContacts("");
        }
      } catch (err) {
        console.error('❌ Failed to fetch users, using demo data:', err);
        setContacts("");
      } finally {
        setContactsLoading(false);
      }
    };
    
    fetchUsers();
  }, [sourceFilter]);

  // Auto-scroll to latest message - reliably scroll container to bottom
  useEffect(() => {
    if (preserveScrollOnConversationChangeRef.current) {
      preserveScrollOnConversationChangeRef.current = false;
      return undefined;
    }

    // Use multiple attempts with increasing delays to ensure DOM is fully rendered
    const scrollToBottom = () => {
      // First try: scroll the parent container directly (more reliable)
      if (messagesAreaRef.current) {
        messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight;
      } else if (containerRef.current) {
        const messagesContainer = containerRef.current.querySelector('[class*="messagesArea"]');
        if (messagesContainer) messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
      // Fallback: use ref scrollIntoView
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
      }
    };

    // Immediate scroll
    scrollToBottom();
    // Delayed scroll to handle async rendering
    const timer1 = setTimeout(scrollToBottom, 50);
    const timer2 = setTimeout(scrollToBottom, 150);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [conversation]);

  // Fetch conversation history for selected contact
  const fetchConversation = async (contact) => {
    if (!contact?.phoneNumber) return;
    if (fetchConversationPhoneRef.current === contact.phoneNumber && loading) return;

    if (fetchConversationAbortRef.current) {
      fetchConversationAbortRef.current.abort();
    }

    const controller = new AbortController();
    fetchConversationAbortRef.current = controller;
    fetchConversationPhoneRef.current = contact.phoneNumber;

    setLoading(true);
    setLoadingOlderMessages(false);
    setConversationPaging(getEmptyConversationPaging());
    setSelectedContact(contact);
    selectedPhoneNumberRef.current = contact.phoneNumber;
    markContactAsRead(contact.phoneNumber);

    setContacts(prevContacts => 
      prevContacts.map(c => 
        isSamePhoneNumber(c.phoneNumber, contact.phoneNumber)
          ? { ...c, unreadCount: 0 }
          : c
      )
    );
    
    if (windowWidth < 768) {
      setShowSidebar(false);
    }
    
    try {
      const elapsedSinceLastFetch = Date.now() - lastConversationFetchAtRef.current;
      if (elapsedSinceLastFetch < MIN_CONVERSATION_FETCH_INTERVAL_MS) {
        await new Promise(resolve => setTimeout(resolve, MIN_CONVERSATION_FETCH_INTERVAL_MS - elapsedSinceLastFetch));
      }
      if (controller.signal.aborted) return;

      lastConversationFetchAtRef.current = Date.now();
      const response = await axiosConfig.post('/callback/conversations', {
        campaignId: '',
        phoneNumber: contact.phoneNumber,
        limit: CONVERSATION_PAGE_LIMIT,
        offset: 0,
      }, { signal: controller.signal });

      if (controller.signal.aborted || fetchConversationPhoneRef.current !== contact.phoneNumber) return;

      if (response.data?.success && Array.isArray(response.data?.data)) {
        const messagesData = response.data.data;
        console.log('✅ Got conversation messages:', messagesData.length);
        
        const firstMessage = messagesData[0];
        if (firstMessage) {
          setSelectedContact(prev => ({
            ...prev,
            phoneNumberId: firstMessage.phonenumberid || prev.phoneNumberId,
            companyId: firstMessage.companyid || prev.companyId,
            username: firstMessage.username || prev.username || prev.name,
            conversationId: firstMessage.conversationid
          }));
        }
        
        const allMessages = transformConversationMessages(messagesData);
        console.log('✅ Transformed messages:', allMessages.length);
        setConversation(allMessages);
        setConversationPaging(buildConversationPaging(response.data, messagesData.length, 0, CONVERSATION_PAGE_LIMIT));
        if (markAsRead) markAsRead(contact.phoneNumber);
      } else {
        console.log('⚠️ No messages from API');
        setConversation([]);
        setConversationPaging(getEmptyConversationPaging());
      }

      setContacts(prevContacts => 
        prevContacts.map(c => 
          isSamePhoneNumber(c.phoneNumber, contact.phoneNumber)
            ? { ...c, unreadCount: 0 }
            : c
        )
      );
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        console.log('🚫 fetchConversation aborted for', contact.phoneNumber, '- superseded by newer request');
        return;
      }
      console.error('Failed to fetch conversation:', err);
      setContacts(prevContacts => 
        prevContacts.map(c => 
          isSamePhoneNumber(c.phoneNumber, contact.phoneNumber)
            ? { ...c, unreadCount: 0 }
            : c
        )
      );
    } finally {
      if (fetchConversationAbortRef.current === controller) {
        fetchConversationAbortRef.current = null;
      }
      if (fetchConversationPhoneRef.current === contact.phoneNumber) {
        setLoading(false);
      }
    }
  };

  const handleContactSelect = (contact) => {
    if (!contact?.phoneNumber) return;
    if (isSamePhoneNumber(selectedPhoneNumberRef.current, contact.phoneNumber) && (loading || conversation.length > 0)) return;

    if (contactSelectDebounceRef.current) {
      clearTimeout(contactSelectDebounceRef.current);
    }

    setChatFilter('all');
    setLoading(true);
    setLoadingOlderMessages(false);
    setConversation([]);
    setConversationPaging(getEmptyConversationPaging());
    setSelectedContact(contact);
    selectedPhoneNumberRef.current = contact.phoneNumber;
    markContactAsRead(contact.phoneNumber);

    setContacts(prevContacts => 
      prevContacts.map(c => 
        isSamePhoneNumber(c.phoneNumber, contact.phoneNumber)
          ? { ...c, unreadCount: 0 }
          : c
      )
    );

    if (windowWidth < 768) {
      setShowSidebar(false);
    }

    contactSelectDebounceRef.current = setTimeout(() => {
      contactSelectDebounceRef.current = null;
      fetchConversation(contact);
    }, CONTACT_SELECT_DEBOUNCE_MS);
  };

  const loadOlderConversationMessages = async () => {
    if (!selectedContact?.phoneNumber || loadingOlderMessagesRef.current || loadingOlderMessages || loading || !conversationPaging.hasMore) return;

    const phoneNumber = selectedContact.phoneNumber;
    const requestedOffset = conversationPaging.nextOffset;
    const requestedLimit = conversationPaging.limit || CONVERSATION_PAGE_LIMIT;
    const previousScrollHeight = messagesAreaRef.current?.scrollHeight || 0;
    const previousScrollTop = messagesAreaRef.current?.scrollTop || 0;

    loadingOlderMessagesRef.current = true;
    setLoadingOlderMessages(true);
    try {
      const response = await axiosConfig.post('/callback/conversations', {
        campaignId: '',
        phoneNumber,
        limit: requestedLimit,
        offset: requestedOffset,
      });

      if (selectedPhoneNumberRef.current !== phoneNumber) return;

      if (response.data?.success && Array.isArray(response.data?.data)) {
        const messagesData = response.data.data;
        const olderMessages = transformConversationMessages(messagesData);
        preserveScrollOnConversationChangeRef.current = true;
        setConversation(prev => mergeConversationMessages(olderMessages, prev));
        setConversationPaging(buildConversationPaging(response.data, messagesData.length, requestedOffset, requestedLimit));

        requestAnimationFrame(() => {
          if (!messagesAreaRef.current) return;
          const heightDiff = messagesAreaRef.current.scrollHeight - previousScrollHeight;
          messagesAreaRef.current.scrollTop = previousScrollTop + heightDiff;
        });
      }
    } catch (err) {
      console.error('Failed to load older conversation messages:', err);
      message.error('Could not load older messages');
    } finally {
      loadingOlderMessagesRef.current = false;
      setLoadingOlderMessages(false);
    }
  };

  // Send reply message (works with demo data too)
  const handleSendReply = async () => {
    if (!reply.trim() || !selectedContact) return;
    
    setSendingMessage(true);
    const messageId = Date.now();
    const messageText = reply;
    
    // Optimistically add message to conversation
    const newMessage = {
      id: messageId,
      text: messageText,
      fromUser: false,
      timestamp: new Date().toISOString(),
      status: 'sending'
    };
    
    setConversation(prev => [...prev, newMessage]);
    setReply('');
    
    try {
      // Also send via HTTP API as fallback
      const response = await axiosConfig.post('/callback/send-reply', {
        mobileno: selectedContact.phoneNumber,
        content: messageText,
        username: selectedContact.username || selectedContact.name,
        companyid: sessionStorage.getItem('customeruuid'),
        campaignid: selectedContact.campaignId,
        conversationid: selectedContact.conversationId
      });

      if (response.data?.statuscode === 200) {
        // Extract server-assigned message ID so WebSocket status updates can match it
        const serverMsgId = response.data?.entity?.conversationid
          || response.data?.entity?.messageId
          || response.data?.data?.conversationid
          || messageId;

        // Update message: promote to 'sent' and store the server ID for WS matching
        setConversation(prev => 
          prev.map(msg => 
            msg.id === messageId
              ? { ...msg, id: serverMsgId, waba_message_id: String(serverMsgId), status: 'sent' }
              : msg
          )
        );
        
        // Update contact's last message
        setContacts(prev => 
          prev.map(contact => 
            isSamePhoneNumber(contact.phoneNumber, selectedContact.phoneNumber) ? 
              {
                ...contact,
                lastMessage: messageText,
                lastMessageTime: new Date().toISOString()
              } : contact
          )
        );
      } else {
        // Non-200 but no error — still mark sent
        setConversation(prev => 
          prev.map(msg => 
            msg.id === messageId ? { ...msg, status: 'sent' } : msg
          )
        );
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      // Update message status to failed
      setConversation(prev => 
        prev.map(msg => 
          msg.id === messageId ? { ...msg, status: 'failed' } : msg
        )
      );
      message.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Handle media file upload and send
  const handleMediaUpload = async (file, caption = '') => {
    if (!selectedContact) {
      message.error('Please select a contact first');
      return;
    }

    setUploadingMedia(true);
    const messageId = Date.now();
    
    try {
      // Determine upload endpoint based on file type
      const fileType = file.type.split('/')[0];
      let endpoint;
      let mediaType;
      let formFieldName;
      
      if (fileType === 'image') {
        endpoint = '/media/upload/image';
        mediaType = 'image';
        formFieldName = 'image';
      } else if (fileType === 'video') {
        endpoint = '/media/upload/video';
        mediaType = 'video';
        formFieldName = 'video';
      } else if (fileType === 'audio') {
        endpoint = '/media/upload/audio';
        mediaType = 'audio';
        formFieldName = 'audio';
      } else {
        endpoint = '/media/upload/document';
        mediaType = 'document';
        formFieldName = 'document';
      }

      // Get username from selectedContact (fetched from /callback/conversations API)
      // This ensures we use the correct username from the conversation data
      const username = selectedContact.username || selectedContact.name || 'User';

      // Create FormData with new payload structure
      const formData = new FormData();
      formData.append(formFieldName, file);
      formData.append('phoneNumber', selectedContact.phoneNumber);
      formData.append('username', username);
      if (caption.trim()) {
        formData.append('caption', caption.trim());
      }
      if (selectedContact.conversationId) {
        formData.append('roomUuid', selectedContact.conversationId);
      }

      // Create local blob URL for immediate preview
      const localPreviewUrl = URL.createObjectURL(file);

      // Optimistically add media message to conversation with local preview
      const newMessage = {
        id: messageId,
        text: caption.trim() || '',
        fromUser: false,
        timestamp: new Date().toISOString(),
        status: 'sending',
        mediaType,
        fileName: file.name,
        fileSize: file.size,
        // Use local preview URL immediately
        localPreviewUrl,
        mediaUrl: localPreviewUrl, // Use local URL as initial mediaUrl
        isImage: mediaType === 'image',
        isVideo: mediaType === 'video',
        isAudio: mediaType === 'audio',
        isDocument: mediaType === 'document'
      };
      
      setConversation(prev => [...prev, newMessage]);

      // Upload file
      const response = await axiosConfig.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data?.status && response.data?.statuscode === 200) {
        // Update message with media URL and success status
        const remoteMediaUrl = response.data.entity.content;
        const displayText = caption.trim() || '';
        
        // Extract server-assigned message ID so WebSocket status updates can match it
        const serverMsgId = response.data.entity.conversationid
          || response.data.entity.messageId
          || messageId;

        setConversation(prev => 
          prev.map(msg => {
            if (msg.id === messageId) {
              // Keep the local preview URL as fallback if remote URL fails
              return { 
                ...msg, 
                // Replace optimistic local ID with server ID so WS STATUS_UPDATE events match
                id: serverMsgId,
                waba_message_id: String(serverMsgId),
                status: 'sent',
                text: displayText,
                mediaUrl: remoteMediaUrl,
                localPreviewUrl: msg.localPreviewUrl, // Keep local preview as fallback
                isImage: mediaType === 'image',
                isVideo: mediaType === 'video',
                isAudio: mediaType === 'audio',
                isDocument: mediaType === 'document'
              };
            }
            return msg;
          })
        );
        
        // Update contact's last message
        setContacts(prev => 
          prev.map(contact => 
            isSamePhoneNumber(contact.phoneNumber, selectedContact.phoneNumber) ? 
              {
                ...contact,
                lastMessage: `📎 ${mediaType.charAt(0).toUpperCase() + mediaType.slice(1)}`,
                lastMessageTime: new Date().toISOString()
              } : contact
          )
        );
        
        message.success(`${mediaType.charAt(0).toUpperCase() + mediaType.slice(1)} sent successfully!`);
      } else {
        throw new Error(response.data?.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Failed to upload media:', err);
      // Update message status to failed
      setConversation(prev => 
        prev.map(msg => 
          msg.id === messageId ? { ...msg, status: 'failed' } : msg
        )
      );
      message.error(`Failed to send ${file.type.split('/')[0]}. ${err.response?.data?.message || err.message || 'Please try again.'}`);
    } finally {
      setUploadingMedia(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle file selection - show preview first
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (25MB limit)
    const maxSize = 25 * 1024 * 1024; // 25MB
    if (file.size > maxSize) {
      message.error('File size must be less than 25MB');
      return;
    }

    // Validate file type
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'video/mp4', 'video/avi', 'video/mov', 'video/wmv',
      'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a',
      'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain', 'text/csv'
    ];

    if (!allowedTypes.includes(file.type)) {
      message.error('Unsupported file type. Please select an image, video, audio, or document file.');
      return;
    }

    // Create preview URL for supported media types
    const fileType = file.type.split('/')[0];
    let url = '';
    if (fileType === 'image' || fileType === 'video' || fileType === 'audio') {
      url = URL.createObjectURL(file);
    }

    setPreviewFile(file);
    setPreviewUrl(url);
    setMediaCaption('');
    setShowMediaPreview(true);
  };

  // Handle attachment button click
  const handleAttachmentClick = () => {
    if (!selectedContact) {
      message.warning('Please select a contact first');
      return;
    }
    fileInputRef.current?.click();
  };

  // Close media preview modal
  const closeMediaPreview = () => {
    setShowMediaPreview(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewFile(null);
    setPreviewUrl('');
    setMediaCaption('');
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Send media after preview confirmation
  const handleSendMedia = () => {
    if (!previewFile) return;
    setShowMediaPreview(false);
    handleMediaUpload(previewFile, mediaCaption);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewFile(null);
    setPreviewUrl('');
    setMediaCaption('');
  };

  // Filter contacts based on search and label filter (guard against null/undefined values)
  const _search = (searchQuery || '').toLowerCase();
  const filteredContacts = Array.isArray(contacts) ? contacts.filter(contact => {
    const name = (contact?.name || '').toLowerCase();
    const phone = (contact?.phoneNumber || '').toLowerCase();
    const matchesSearch = name.includes(_search) || phone.includes(_search);
    // Client-side source filter — acts as a reliable fallback when the backend
    // /callback/conversations/users endpoint returns all contacts regardless of
    // the externalStatus query param. Each contact's externalStatus is set from
    // the backend's external_status / is_external_api fields via getExternalStatus().
    const extStatus = Number(contact?.externalStatus ?? 0);
    const matchesSource = sourceFilter === 'all'
      || (sourceFilter === 'internal'   && extStatus === 0)
      || (sourceFilter === 'external'   && extStatus === 1)
      || (sourceFilter === 'suppressed' && contact?.suppressedAutomation === true);
    if (!matchesSearch || !matchesSource) return false;
    if (labelFilter && labelFilter.length > 0) {
      const contactLabels = (contact?.labels || []).map(l => (l || '').toLowerCase().trim());
      return labelFilter.some(fl => contactLabels.includes(fl.toLowerCase().trim()));
    }
    return true;
  }) : [];

  // Apply per-chat filter before grouping:
  //   all      → show everything
  //   internal → external_status === 0 (platform messages)
  //   external → external_status === 1 (transactional / external API)
  //   ai       → section_owner === 'ai'  (whole AI-handled section, incl. user messages inside it)
  //   bot      → section_owner === 'bot' (bot-flow section)
  const filteredConversation = useMemo(() => {
    if (chatFilter === 'all') return conversation;
    return conversation.filter(msg => {
      if (chatFilter === 'internal') return Number(msg.externalStatus ?? msg.external_status ?? 0) === 0;
      if (chatFilter === 'external') return Number(msg.externalStatus ?? msg.external_status ?? 0) === 1;
      if (chatFilter === 'ai')       return (msg.section_owner || msg.sectionOwner) === 'ai';
      if (chatFilter === 'bot')      return (msg.section_owner || msg.sectionOwner) === 'bot';
      return true;
    });
  }, [conversation, chatFilter]);

  // Group messages by calendar date — injects DateSeparator items between day boundaries
  const groupedMessages = useMemo(() => groupMessagesWithSections(filteredConversation), [filteredConversation]);

  // Open media viewer (lightbox) for images/videos
  const openMediaViewer = (url, type = 'image', fallbackUrl = '') => {
    setMediaViewerUrl(url);
    setMediaViewerFallbackUrl(fallbackUrl);
    setMediaViewerType(type);
    setImageZoom(1);
    setImagePan({ x: 0, y: 0 });
    setMediaViewerOpen(true);
  };

  // Close media viewer
  const closeMediaViewer = () => {
    setMediaViewerOpen(false);
    setMediaViewerUrl('');
    setMediaViewerType('image');
    setImageZoom(1);
    setImagePan({ x: 0, y: 0 });
  };

  // Zoom configuration
  const MIN_ZOOM = 0.25;
  const MAX_ZOOM = 5;
  const ZOOM_STEP = 0.25;
  
  const handleZoomIn = () => {
    setImageZoom(prev => {
      const newZoom = Math.min(prev + ZOOM_STEP, MAX_ZOOM);
      // Reset pan if zooming back to 1 or below
      if (newZoom <= 1) setImagePan({ x: 0, y: 0 });
      return newZoom;
    });
  };
  
  const handleZoomOut = () => {
    setImageZoom(prev => {
      const newZoom = Math.max(prev - ZOOM_STEP, MIN_ZOOM);
      // Reset pan if zooming to 1 or below
      if (newZoom <= 1) setImagePan({ x: 0, y: 0 });
      return newZoom;
    });
  };
  
  const handleResetZoom = () => {
    setImageZoom(1);
    setImagePan({ x: 0, y: 0 });
  };

  // Handle mouse wheel zoom
  const handleWheelZoom = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setImageZoom(prev => {
      const newZoom = Math.min(Math.max(prev + delta, MIN_ZOOM), MAX_ZOOM);
      if (newZoom <= 1) setImagePan({ x: 0, y: 0 });
      return newZoom;
    });
  };

  // Handle drag/pan start
  const handleDragStart = (e) => {
    if (imageZoom <= 1) return;
    e.preventDefault();
    setIsDragging(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragStart({ x: clientX - imagePan.x, y: clientY - imagePan.y });
  };

  // Handle drag/pan move
  const handleDragMove = (e) => {
    if (!isDragging || imageZoom <= 1) return;
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setImagePan({
      x: clientX - dragStart.x,
      y: clientY - dragStart.y
    });
  };

  // Handle drag/pan end
  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Double click to toggle zoom
  const handleDoubleClick = () => {
    if (imageZoom === 1) {
      setImageZoom(2);
    } else {
      setImageZoom(1);
      setImagePan({ x: 0, y: 0 });
    }
  };

  // Keyboard shortcuts for zoom
  useEffect(() => {
    if (!mediaViewerOpen || mediaViewerType !== 'image') return;
    
    const handleKeyDown = (e) => {
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        handleZoomIn();
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        handleZoomOut();
      } else if (e.key === '0') {
        e.preventDefault();
        handleResetZoom();
      } else if (e.key === 'Escape') {
        closeMediaViewer();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mediaViewerOpen, mediaViewerType]);

  // Format time for display
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { 
        day: '2-digit', 
        month: '2-digit',
        year: '2-digit' 
      });
    }
  };

  // Skeletal loading for contacts
  const ContactSkeleton = ({ count = 8 }) => (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} style={{
          padding: windowWidth < 480 ? '8px 6px' : '8px 8px',
          margin: windowWidth < 480 ? '0 6px' : '0 8px',
          display: 'flex',
          alignItems: 'center',
          marginBottom: 8
        }}>
          <div style={{
            width: windowWidth < 480 ? 44 : 56,
            height: windowWidth < 480 ? 44 : 56,
            borderRadius: '50%',
            background: chatTheme === 'light' ? '#f0f0f0' : '#333',
            marginRight: 12,
            flexShrink: 0,
            animation: 'skeleton-loading 1.5s infinite ease-in-out'
          }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{
                height: 16,
                background: chatTheme === 'light' ? '#f0f0f0' : '#333',
                borderRadius: 4,
                width: '60%',
                animation: 'skeleton-loading 1.5s infinite ease-in-out'
              }} />
              <div style={{
                height: 12,
                background: chatTheme === 'light' ? '#f0f0f0' : '#333',
                borderRadius: 4,
                width: '20%',
                animation: 'skeleton-loading 1.5s infinite ease-in-out'
              }} />
            </div>
            <div style={{
              height: 14,
              background: chatTheme === 'light' ? '#f0f0f0' : '#333',
              borderRadius: 4,
              width: '80%',
              animation: 'skeleton-loading 1.5s infinite ease-in-out'
            }} />
          </div>
        </div>
      ))}
    </>
  );

  // Skeletal loading for messages
  const MessageSkeleton = ({ count = 7 }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '20px 0' }}>
      {Array.from({ length: count }).map((_, index) => {
        const isFromUser = index % 3 === 0;
        const widths = ['55%', '40%', '65%', '35%', '50%', '45%', '60%'];
        const w = widths[index % widths.length];
        return (
          <div key={index} style={{
            display: 'flex',
            justifyContent: isFromUser ? 'flex-start' : 'flex-end',
            marginBottom: 4,
          }}>
            {isFromUser && (
              <div style={{
                width: 32, height: 32, borderRadius: '50%', flexShrink: 0, marginRight: 8, alignSelf: 'flex-end',
                background: chatTheme === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)',
                animation: 'skeleton-loading 1.5s infinite ease-in-out',
              }} />
            )}
            <div style={{
              maxWidth: '65%',
              width: w,
              padding: '10px 14px 8px',
              borderRadius: isFromUser ? '8px 8px 8px 0' : '8px 8px 0 8px',
              background: chatTheme === 'light' ? 'rgba(255,255,255,0.75)' : 'rgba(32,44,51,0.75)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
              animation: 'skeleton-loading 1.5s infinite ease-in-out',
            }}>
              <div style={{
                height: 13,
                background: chatTheme === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.12)',
                borderRadius: 6,
                marginBottom: 7,
                width: '85%',
              }} />
              <div style={{
                height: 13,
                background: chatTheme === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.12)',
                borderRadius: 6,
                marginBottom: 7,
                width: '60%',
              }} />
              <div style={{
                height: 10,
                background: chatTheme === 'light' ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.08)',
                borderRadius: 4,
                width: '30%',
                marginLeft: 'auto',
              }} />
            </div>
          </div>
        );
      })}
    </div>
  );

  // ── Label Contact (from Inbox) ────────────────────────────────────────────
  const openLabelContactModal = async () => {
    setLabelContactModalOpen(true);
    labelContactForm.resetFields();
    setLabelContactSelectedBook(null);
    // Pre-populate with existing label if contact already has one
    const existingLabels = selectedContact?.labels || [];
    const predefinedValues = predefinedLabels.map(l => l.value);
    const existingPredefined = existingLabels.filter(l => predefinedValues.includes(l));
    const existingCustom = existingLabels.find(l => !predefinedValues.includes(l)) || '';
    if (existingPredefined.length || existingCustom) {
      labelContactForm.setFieldsValue({
        labelTypes: existingPredefined,
        customLabel: existingCustom,
      });
    }
    setLabelContactBooksLoading(false);
  };

  const handleLabelContactSubmit = async (values) => {
    if (!selectedContact?.phoneNumber) return;
    const { labelTypes, customLabel } = values;
    const allLabels = [...(labelTypes || [])];
    if (customLabel && customLabel.trim()) allLabels.push(customLabel.trim());

    // If no labels selected — treat as remove all labels
    if (!allLabels.length) {
      await handleRemoveLabel();
      return;
    }

    setLabelContactLoading(true);
    try {
      const enrichedContact = contacts.find(c => c.phoneNumber === selectedContact.phoneNumber) || selectedContact;
      const isAddressbook = !!enrichedContact.addressbookUserId && !!enrichedContact.addressbookBookId;

      if (isAddressbook) {
        // Addressbook contact — use label-users API to assign labels
        const res = await axiosConfig.post('/addressbook/label-users', {
          bookid: enrichedContact.addressbookBookId,
          userids: [enrichedContact.addressbookUserId],
          label: allLabels,
        });
        if (res.data?.statuscode === 200 || res.data?.success) {
          message.success('Labels updated');
          setContacts((prev) => prev.map((c) =>
            c.phoneNumber === selectedContact.phoneNumber ? { ...c, labels: allLabels } : c
          ));
          setSelectedContact(prev => ({ ...prev, labels: allLabels }));
          setLabelContactModalOpen(false);
          labelContactForm.resetFields();
        } else {
          message.error(res.data?.message || 'Failed to update labels');
        }
      } else {
        // External/non-addressbook — use inbox assign-label API (single label only)
        const rawPhone = selectedContact.phoneNumber.replace(/\D/g, '');
        const labelRes = await axiosConfig.patch('/callback/conversations/assign-label', {
          mobileno: rawPhone,
          label: allLabels[0],
        });
        if (labelRes.data?.success) {
          message.success(`Label "${allLabels[0]}" assigned`);
          setContacts((prev) => prev.map((c) =>
            c.phoneNumber === selectedContact.phoneNumber ? { ...c, labels: [allLabels[0]] } : c
          ));
          setLabelContactModalOpen(false);
          labelContactForm.resetFields();
        } else {
          message.error(labelRes.data?.message || 'Failed to assign label');
        }
      }
    } catch (err) {
      console.error('Label contact error:', err);
      message.error('Something went wrong while assigning labels');
    } finally {
      setLabelContactLoading(false);
    }
  };

  const handleRemoveLabel = async () => {
    if (!selectedContact?.phoneNumber) return;
    setLabelContactLoading(true);
    try {
      // Always look up the enriched contact from contacts array — selectedContact
      // may have been set before label enrichment ran (enrichment only updates contacts[],
      // not selectedContact), so addressbookUserId/BookId could be missing on selectedContact.
      const enrichedContact = contacts.find(c => c.phoneNumber === selectedContact.phoneNumber) || selectedContact;
      const isAddressbook = !!enrichedContact.addressbookUserId && !!enrichedContact.addressbookBookId;

      if (isAddressbook) {
        // Contact is in addressbook — clear label via addressbook API
        const res = await axiosConfig.post('/addressbook/remove-label', {
          bookid: enrichedContact.addressbookBookId,
          userids: [enrichedContact.addressbookUserId],
        });
        if (res.data?.statuscode === 200 || res.data?.success) {
          message.success('Label removed');
          setContacts((prev) => prev.map((c) =>
            c.phoneNumber === selectedContact.phoneNumber
              ? { ...c, labels: [], addressbookUserId: c.addressbookUserId, addressbookBookId: c.addressbookBookId }
              : c
          ));
          setSelectedContact(prev => ({ ...prev, labels: [] }));
          setLabelContactModalOpen(false);
          labelContactForm.resetFields();
        } else {
          message.error(res.data?.message || 'Failed to remove label');
        }
      } else {
        // External/non-addressbook contact — clear via inbox label API
        const rawPhone = selectedContact.phoneNumber.replace(/\D/g, '');
        const res = await axiosConfig.patch('/callback/conversations/assign-label', {
          mobileno: rawPhone,
          label: '',
        });
        if (res.data?.success) {
          message.success('Label removed');
          setContacts((prev) => prev.map((c) =>
            c.phoneNumber === selectedContact.phoneNumber
              ? { ...c, labels: [] }
              : c
          ));
          setLabelContactModalOpen(false);
          labelContactForm.resetFields();
        } else {
          message.error(res.data?.message || 'Failed to remove label');
        }
      }
    } catch (err) {
      console.error('Remove label error:', err);
      message.error('Something went wrong');
    } finally {
      setLabelContactLoading(false);
    }
  };

  return (
    <>
      {/* Global styles for animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes skeleton-loading {
          0%   { opacity: 1; }
          50%  { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    <div 
        ref={containerRef}
        data-theme={chatTheme}
        style={{ 
          height: 'calc(100vh - 84px)',
          width: '100%',
          margin: 0,
          padding: 0,
          display: 'flex', 
          background: chatTheme === 'light' ? '#fff' : '#1a1a1a',
          borderRadius: 0,
          overflow: 'hidden',
          boxShadow: 'none',
          position: 'relative',
          zIndex: windowWidth < 768 ? 1001 : 1
        }} className={styles.inboxRoot}>
      {/* Left Panel - Contacts List */}
      <div style={{ 
        width: windowWidth < 768 ? '100%' : sidebarWidth, 
        background: chatTheme === 'light' ? '#fff' : '#111b21', 
        borderRight: windowWidth < 768 ? 'none' : (chatTheme === 'light' ? '1px solid #e9edef' : '1px solid #222d34'),
        display: windowWidth < 768 ? (showSidebar ? 'flex' : 'none') : 'flex',
        flexDirection: 'column',
        height: '100%',
        transition: isResizing ? 'none' : 'width 0.2s ease',
        position: 'relative',
        zIndex: 1
      }} className={styles.sidebar}>
        {/* Header */}
        <div style={{ 
          padding: windowWidth < 480 ? '16px' : '20px', 
          borderBottom: chatTheme === 'light' ? '1px solid #e9edef' : '1px solid #333',
          background: CHAT_THEMES[colorTheme][chatTheme].headerBg || (chatTheme === 'light' 
            ? '#f0f2f5' 
            : '#202c33')
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: windowWidth < 480 ? 8 : 12 }}>
              {windowWidth < 768 && (
                <Button 
                  type="text" 
                  icon={<MenuOutlined />}
                  onClick={openMobileDrawer}
                  size="large"
                  style={{ 
                    color: chatTheme === 'light' ? '#54656f' : '#aebac1',
                    padding: '4px 8px',
                    minWidth: 40,
                    height: 40,
                    fontSize: 18,
                    flexShrink: 0
                  }}
                />
              )}
              <Title level={4} style={{ 
                margin: 0, 
                fontSize: windowWidth < 480 ? 18 : 22, 
                fontWeight: 700,
                color: chatTheme === 'light' ? '#111b21' : '#e9edef'
              }}>
                Chats
              </Title>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <Tooltip title={notificationSoundEnabled ? 'Mute notifications' : 'Unmute notifications'}>
                <Button 
                  type="text" 
                  icon={notificationSoundEnabled ? <SoundOutlined /> : <SoundTwoTone twoToneColor="#ff4d4f" />}
                  size={windowWidth < 480 ? 'middle' : 'large'}
                  style={{ color: notificationSoundEnabled ? CHAT_THEMES[colorTheme][chatTheme].accent : '#ff4d4f' }}
                  onClick={() => {
                    setNotificationSoundEnabled(!notificationSoundEnabled);
                  }}
                />
              </Tooltip>
              <Dropdown
                trigger={['click']}
                placement="bottomRight"
                menu={{
                  items: [
                    {
                      key: 'theme-header',
                      label: (
                        <div style={{ 
                          fontSize: 11, 
                          fontWeight: 700, 
                          color: '#8696a0', 
                          textTransform: 'uppercase', 
                          letterSpacing: '0.5px',
                          padding: '4px 0',
                          cursor: 'default',
                          pointerEvents: 'none'
                        }}>
                          🎨 Color Theme
                        </div>
                      ),
                      disabled: true,
                    },
                    {
                      key: 'whatsapp',
                      label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#25D366', display: 'inline-block' }} />
                          <span>WhatsApp Green</span>
                          {colorTheme === 'whatsapp' && <span style={{ marginLeft: 'auto', color: '#25D366' }}>✓</span>}
                        </div>
                      ),
                      onClick: () => updateColorTheme('whatsapp'),
                    },
                    {
                      key: 'ocean',
                      label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'linear-gradient(135deg, #0077b6, #00b4d8)', display: 'inline-block' }} />
                          <span>Ocean Blue</span>
                          {colorTheme === 'ocean' && <span style={{ marginLeft: 'auto', color: '#0084ff' }}>✓</span>}
                        </div>
                      ),
                      onClick: () => updateColorTheme('ocean'),
                    },
                    {
                      key: 'sunset',
                      label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'linear-gradient(135deg, #ff6b6b, #feca57)', display: 'inline-block' }} />
                          <span>Sunset Orange</span>
                          {colorTheme === 'sunset' && <span style={{ marginLeft: 'auto', color: '#f5576c' }}>✓</span>}
                        </div>
                      ),
                      onClick: () => updateColorTheme('sunset'),
                    },
                    {
                      key: 'forest',
                      label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'linear-gradient(135deg, #2d6a4f, #40916c)', display: 'inline-block' }} />
                          <span>Forest Green</span>
                          {colorTheme === 'forest' && <span style={{ marginLeft: 'auto', color: '#11998e' }}>✓</span>}
                        </div>
                      ),
                      onClick: () => updateColorTheme('forest'),
                    },
                    {
                      key: 'midnight',
                      label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea, #764ba2)', display: 'inline-block' }} />
                          <span>Midnight Purple</span>
                          {colorTheme === 'midnight' && <span style={{ marginLeft: 'auto', color: '#667eea' }}>✓</span>}
                        </div>
                      ),
                      onClick: () => updateColorTheme('midnight'),
                    },
                    { type: 'divider' },
                    {
                      key: 'mode-header',
                      label: (
                        <div style={{ 
                          fontSize: 11, 
                          fontWeight: 700, 
                          color: '#8696a0', 
                          textTransform: 'uppercase', 
                          letterSpacing: '0.5px',
                          padding: '4px 0',
                          cursor: 'default',
                          pointerEvents: 'none'
                        }}>
                          🌓 Display Mode
                        </div>
                      ),
                      disabled: true,
                    },
                    {
                      key: 'light-mode',
                      label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span>☀️ Light Mode</span>
                          {chatTheme === 'light' && <span style={{ marginLeft: 'auto', color: '#f9a825' }}>✓</span>}
                        </div>
                      ),
                      onClick: () => updateDisplayMode('light'),
                    },
                    {
                      key: 'dark-mode',
                      label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span>🌙 Dark Mode</span>
                          {chatTheme === 'dark' && <span style={{ marginLeft: 'auto', color: '#5c6bc0' }}>✓</span>}
                        </div>
                      ),
                      onClick: () => updateDisplayMode('dark'),
                    },
                  ],
                }}
              >
                <Tooltip title="More options">
                  <Button 
                    type="text" 
                    icon={<MoreOutlined />}
                    size={windowWidth < 480 ? 'middle' : 'large'}
                    style={{ color: CHAT_THEMES[colorTheme][chatTheme].accent }}
                  />
                </Tooltip>
              </Dropdown>
            </div>
          </div>
        </div>

        {/* Search + Label Filter */}
        <div style={{ padding: windowWidth < 480 ? '12px 12px' : '16px 16px', display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'center' }}>
          <Input
            placeholder="Search conversations..."
            prefix={<SearchOutlined style={{ color: CHAT_THEMES[colorTheme][chatTheme].accent }} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={chatTheme === 'dark' ? styles.darkThemePlaceholder : ''}
            style={{ 
              flex: 1,
              borderRadius: 8,
              background: chatTheme === 'light' ? '#f0f2f5' : '#202c33',
              border: 'none',
              padding: '8px 12px',
              fontSize: windowWidth < 480 ? 13 : 14,
              color: chatTheme === 'light' ? '#111b21' : '#e9edef'
            }}
          />
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            dropdownRender={() => (
              <div style={{
                background: chatTheme === 'light' ? '#fff' : '#1f2c34',
                borderRadius: 12,
                boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
                padding: '6px 0 8px',
                minWidth: 196,
                border: chatTheme === 'light' ? '1px solid #e8ecef' : '1px solid #2a3942',
              }}>
                <div style={{ padding: '6px 14px 8px', fontSize: 11, fontWeight: 600, color: '#8696a0', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  Filter by label
                </div>
                {predefinedLabels.map((lbl) => {
                  const isActive = labelFilter.includes(lbl.value);
                  return (
                    <div
                      key={lbl.value}
                      onClick={() => setLabelFilter(prev =>
                        prev.includes(lbl.value) ? prev.filter(v => v !== lbl.value) : [...prev, lbl.value]
                      )}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '9px 14px', cursor: 'pointer', borderRadius: 8, margin: '1px 6px',
                        background: isActive
                          ? (chatTheme === 'light' ? 'rgba(102,126,234,0.1)' : 'rgba(102,126,234,0.18)')
                          : 'transparent',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = chatTheme === 'light' ? '#f5f6f8' : '#2a3942'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = isActive ? (chatTheme === 'light' ? 'rgba(102,126,234,0.1)' : 'rgba(102,126,234,0.18)') : 'transparent'; }}
                    >
                      <span style={{ fontSize: 15, color: isActive ? CHAT_THEMES[colorTheme][chatTheme].accent : (chatTheme === 'light' ? '#54656f' : '#aebac1') }}>
                        {lbl.icon}
                      </span>
                      <span style={{ flex: 1, fontSize: 13.5, fontWeight: isActive ? 500 : 400, color: chatTheme === 'light' ? '#111b21' : '#e9edef' }}>
                        {lbl.label}
                      </span>
                      {isActive && (
                        <span style={{ color: CHAT_THEMES[colorTheme][chatTheme].accent, fontSize: 12, fontWeight: 700 }}>✓</span>
                      )}
                    </div>
                  );
                })}
                {labelFilter.length > 0 && (
                  <>
                    <div style={{ height: 1, background: chatTheme === 'light' ? '#f0f2f5' : '#2a3942', margin: '6px 8px' }} />
                    <div
                      onClick={() => setLabelFilter([])}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 14px', cursor: 'pointer', borderRadius: 8, margin: '1px 6px',
                        color: '#ef4444', fontSize: 13, fontWeight: 500,
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      Clear all filters
                    </div>
                  </>
                )}
              </div>
            )}
          >
            <Badge dot={labelFilter.length > 0} color={CHAT_THEMES[colorTheme][chatTheme].accent}>
              <Button
                type="text"
                icon={<FilterOutlined />}
                style={{
                  borderRadius: 8,
                  background: labelFilter.length > 0
                    ? (chatTheme === 'light' ? 'rgba(102, 126, 234, 0.12)' : 'rgba(102, 126, 234, 0.2)')
                    : (chatTheme === 'light' ? '#f0f2f5' : '#202c33'),
                  color: labelFilter.length > 0
                    ? CHAT_THEMES[colorTheme][chatTheme].accent
                    : (chatTheme === 'light' ? '#54656f' : '#aebac1'),
                  width: 36, height: 36,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, border: 'none',
                }}
              />
            </Badge>
          </Dropdown>
          
          <Dropdown
            menu={{
              items: [
                { key: 'source-header', type: 'group', label: 'Filter Source' },
                { 
                  key: 'all', 
                  label: <span style={{ fontWeight: sourceFilter === 'all' ? 700 : 400, color: sourceFilter === 'all' ? CHAT_THEMES[colorTheme][chatTheme].accent : 'inherit' }}>All</span>, 
                  onClick: () => {
                    setSourceFilter('all');
                    setSelectedContact(null);
                    setConversation([]);
                  }
                },
                { 
                  key: 'internal', 
                  label: <span style={{ fontWeight: sourceFilter === 'internal' ? 700 : 400, color: sourceFilter === 'internal' ? CHAT_THEMES[colorTheme][chatTheme].accent : 'inherit' }}>Internal</span>, 
                  onClick: () => {
                    setSourceFilter('internal');
                    setSelectedContact(null);
                    setConversation([]);
                  }
                },
                { 
                  key: 'external', 
                  label: <span style={{ fontWeight: sourceFilter === 'external' ? 700 : 400, color: sourceFilter === 'external' ? CHAT_THEMES[colorTheme][chatTheme].accent : 'inherit' }}>External API</span>, 
                  onClick: () => {
                    setSourceFilter('external');
                    setSelectedContact(null);
                    setConversation([]);
                  }
                },
                {
                  key: 'suppressed',
                  label: <span style={{ fontWeight: sourceFilter === 'suppressed' ? 700 : 400, color: sourceFilter === 'suppressed' ? '#dc2626' : 'inherit' }}>Suppressed Replies</span>,
                  onClick: () => {
                    setSourceFilter('suppressed');
                    setSelectedContact(null);
                    setConversation([]);
                  }
                }
              ]
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              icon={<AppstoreOutlined />}
              title="Filter by Source"
              style={{
                borderRadius: '8px',
                background: chatTheme === 'light' ? '#f0f2f5' : '#202c33',
                color: chatTheme === 'light' ? '#54656f' : '#aebac1',
                width: 36, height: 36,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, border: 'none',
              }}
            />
          </Dropdown>
        </div>
        <div 
          style={{ flex: 1, overflow: 'auto', padding: '0 8px', marginTop: '4px' }} 
          className={`${styles.contactsList} ${chatTheme === 'dark' ? styles.darkScrollbar : ''}`}
        >
          {contactsLoading ? (
            <ContactSkeleton />
          ) : filteredContacts.length > 0 ? (
            filteredContacts.map((contact) => (
              <div
                key={contact.phoneNumber}
                onClick={() => handleContactSelect(contact)}
                style={{
                  padding: windowWidth < 480 ? '6px 8px' : '7px 9px',
                  marginBottom: 1,
                  cursor: 'pointer',
                  background: isSamePhoneNumber(selectedContact?.phoneNumber, contact.phoneNumber)
                    ? (chatTheme === 'light' 
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.08) 100%)' 
                        : 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.15) 100%)'
                      ) 
                    : 'transparent',
                  borderRadius: 10,
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  border: isSamePhoneNumber(selectedContact?.phoneNumber, contact.phoneNumber)
                    ? '1px solid rgba(102, 126, 234, 0.2)' 
                    : '1px solid transparent'
                }}
                onMouseEnter={(e) => {
                  if (!isSamePhoneNumber(selectedContact?.phoneNumber, contact.phoneNumber)) {
                    e.currentTarget.style.background = chatTheme === 'light' 
                      ? 'rgba(102, 126, 234, 0.06)' 
                      : 'rgba(102, 126, 234, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSamePhoneNumber(selectedContact?.phoneNumber, contact.phoneNumber)) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <div style={{ position: 'relative', marginRight: 10, flexShrink: 0 }}>
                  <Avatar 
                    size={windowWidth < 480 ? 34 : 38}
                    style={{ 
                      background: getAvatarStyle(contact.phoneNumber, colorTheme).bg, 
                      color: getAvatarStyle(contact.phoneNumber, colorTheme).color,
                      fontSize: windowWidth < 480 ? 12 : 13,
                      fontWeight: 600,
                      border: '2px solid rgba(255,255,255,0.4)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                  >
                    {getInitials(contact.name)}
                  </Avatar>
                </div>
                
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 6, flex: 1 }}>
                      <Text strong style={{ 
                        fontSize: windowWidth < 480 ? 12.5 : 13.5, 
                        color: chatTheme === 'light' ? '#111b21' : '#fff',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {contact.name}
                      </Text>
                    </div>
                    <Text style={{ 
                      fontSize: windowWidth < 480 ? 9 : 10, 
                      color: contact.unreadCount > 0 
                        ? (chatTheme === 'light' ? '#111b21' : '#fff')
                        : (chatTheme === 'light' ? '#999' : '#aaa'),
                      flexShrink: 0,
                      marginLeft: 8
                    }}>
                      {formatTime(contact.lastMessageTime)}
                    </Text>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ 
                      fontSize: windowWidth < 480 ? 10.5 : 11.5, 
                      color: contact.unreadCount > 0 
                        ? (chatTheme === 'light' ? '#111b21' : '#fff')
                        : (chatTheme === 'light' ? '#999' : '#aaa'),
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                      fontWeight: contact.unreadCount > 0 ? 500 : 400
                    }}>
                      {contact.lastMessage}
                    </Text>
                    {contact.unreadCount > 0 && (
                      <div style={{
                        background: CHAT_THEMES[colorTheme][chatTheme].accent,
                        color: '#fff',
                        borderRadius: 10,
                        minWidth: 20,
                        height: 20,
                        padding: '0 6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: 600,
                        marginLeft: 8,
                        flexShrink: 0
                      }}>
                        {contact.unreadCount}
                      </div>
                    )}
                  </div>
                  {getRepliedToTemplateLabel(contact.repliedToTemplate) && (
                    <Text
                      title={`Replied to: ${getRepliedToTemplateLabel(contact.repliedToTemplate)}`}
                      style={{
                        display: 'block',
                        marginTop: 2,
                        fontSize: windowWidth < 480 ? 10 : 11,
                        color: CHAT_THEMES[colorTheme][chatTheme].accent,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontWeight: 600,
                      }}
                    >
                      Replied to: {getRepliedToTemplateLabel(contact.repliedToTemplate)}
                    </Text>
                  )}
                </div>
              </div>
            ))
          ) : (
            <Empty
              description={
                <div style={{
                  marginTop: 8,
                  color: chatTheme === 'light' ? '#6b7280' : '#d1d5db',
                  fontSize: 14
                }}>
                  {sourceFilter === 'external' ? 'No External API conversations found' : sourceFilter === 'internal' ? 'No internal conversations found' : sourceFilter === 'suppressed' ? 'No suppressed (one-way campaign) replies' : 'No contacts found'}
                </div>
              }
              style={{ marginTop: 60 }}
              image={null}
            />
          )}
        </div>
      </div>

      {/* Resizable Divider - Hide on mobile */}
      {windowWidth >= 768 && (
        <div
          onMouseDown={handleMouseDown}
          style={{
            width: 6,
            background: isResizing 
              ? (chatTheme === 'light' ? '#0000009f' : '#ddd') 
              : (chatTheme === 'light' ? '#e5e5e5' : '#333'),
            cursor: 'col-resize',
            transition: isResizing ? 'none' : 'background 0.15s ease',
            userSelect: 'none',
            ':hover': { background: chatTheme === 'light' ? '#000' : '#ddd' }
          }}
          onMouseEnter={(e) => {
            if (!isResizing) {
              e.currentTarget.style.background = chatTheme === 'light' ? '#818181ff' : '#ddd';
            }
          }}
          onMouseLeave={(e) => {
            if (!isResizing) {
              e.currentTarget.style.background = chatTheme === 'light' ? '#e5e5e5' : '#6b6b6bff';
            }
          }}
          className={styles.divider}
        />
      )}

      {/* Right Panel - Chat */}
      <div style={{ 
        flex: 1, 
        display: windowWidth < 768 ? (showSidebar ? 'none' : 'flex') : 'flex', 
        flexDirection: 'column',
        background: chatTheme === 'light' ? '#fff' : '#1a1a1a',
        height: '100%',
        minHeight: 0,
        width: windowWidth < 768 ? '100%' : 'auto'
      }} className={styles.chatContent}>
        {selectedContact ? (
          <div style={{ 
            position: 'relative',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            background: chatTheme === 'light' 
              ? '#fff' 
              : '#1a1a1a',
            backgroundImage: `url(${chatTheme === 'light' ? chatBgLight : chatBgDark})`,
            backgroundSize: 'auto',
            backgroundPosition: 'top left',
            backgroundRepeat: 'repeat',
            backgroundAttachment: 'scroll',
            minHeight: 0
          }}>
            {/* Chat Header */}
            <div style={{ 
              padding: windowWidth < 480 ? '12px 16px' : '12px 20px', 
              background: CHAT_THEMES[colorTheme][chatTheme].headerBg || (chatTheme === 'light' ? '#f0f2f5' : '#202c33'),
              borderBottom: '1px solid ' + (chatTheme === 'light' ? '#e9edef' : '#333'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: windowWidth < 480 ? 50 : 60,
              flexShrink: 0,
              zIndex: 100
            }} className={styles.stickyHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: windowWidth < 480 ? 8 : 12, flex: 1, minWidth: 0 }}>
                {windowWidth < 768 && (
                  <Button 
                    type="text" 
                    onClick={() => {
                      setShowSidebar(true);
                    }}
                    size="large"
                    style={{ 
                      color: chatTheme === 'light' ? '#54656f' : '#ddd',
                      padding: '4px 8px',
                      minWidth: 40,
                      height: 40,
                      fontSize: 18,
                      flexShrink: 0
                    }}
                  >
                    ←
                  </Button>
                )}
                <Avatar 
                  size={windowWidth < 480 ? 38 : 48}
                  style={{ 
                    background: getAvatarStyle(selectedContact.phoneNumber, colorTheme).bg, 
                    color: getAvatarStyle(selectedContact.phoneNumber, colorTheme).color,
                    fontSize: windowWidth < 480 ? 13 : 16,
                    fontWeight: 600,
                    border: '2px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    flexShrink: 0
                  }}
                >
                  {getInitials(selectedContact.name)}
                </Avatar>
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                  <Text strong style={{ 
                    fontSize: windowWidth < 480 ? 14 : 15, 
                    color: chatTheme === 'light' ? '#111b21' : '#fff', 
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {selectedContact.name}
                  </Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Text style={{ 
                      fontSize: windowWidth < 480 ? 11 : 12, 
                      color: chatTheme === 'light' ? '#999' : '#aaa',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {selectedContact.lastMessageTime 
                        ? formatTime(selectedContact.lastMessageTime)
                        : selectedContact.phoneNumber
                      }
                    </Text>
                    {wsConnected ? (
                      <div style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#25d366',
                        marginLeft: 4
                      }} title="Connected" />
                    ) : (
                      <div style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#ff4d4f',
                        marginLeft: 4
                      }} title="Disconnected" />
                    )}
                  </div>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: windowWidth < 480 ? 2 : 8, flexShrink: 0 }}>
                <Button 
                  type="text" 
                  icon={<SunOutlined />}
                  size="large"
                  style={{ 
                    color: chatTheme === 'light' ? '#54656f' : '#ddd',
                    padding: windowWidth < 480 ? '4px 6px' : '4px 8px',
                    minWidth: windowWidth < 480 ? 36 : 40
                  }}
                  onClick={() => updateDisplayMode(chatTheme === 'light' ? 'dark' : 'light')}
                  title="Toggle theme"
                />
                <Tooltip title="Label this contact">
                  <Button
                    type="text"
                    icon={<TagOutlined />}
                    size="large"
                    style={{
                      color: chatTheme === 'light' ? '#54656f' : '#ddd',
                      padding: windowWidth < 480 ? '4px 6px' : '4px 8px',
                      minWidth: windowWidth < 480 ? 36 : 40
                    }}
                    onClick={openLabelContactModal}
                  />
                </Tooltip>
                {windowWidth >= 480 && (
                  <Button 
                    type="text" 
                    icon={<SearchOutlined />} 
                    size="large" 
                    style={{ color: chatTheme === 'light' ? '#54656f' : '#ddd', minWidth: 40 }} 
                  />
                )}
                <Dropdown
                  menu={{
                    items: [
                      { key: 'filter-header', type: 'group', label: 'Filter Messages' },
                      { 
                        key: 'all', 
                        label: <span style={{ fontWeight: chatFilter === 'all' ? 700 : 400, color: chatFilter === 'all' ? '#2563eb' : 'inherit' }}>All Messages</span>, 
                        onClick: () => setChatFilter('all') 
                      },
                      { 
                        key: 'internal', 
                        label: <span style={{ fontWeight: chatFilter === 'internal' ? 700 : 400, color: chatFilter === 'internal' ? '#16a34a' : 'inherit' }}>Internal</span>, 
                        onClick: () => setChatFilter('internal') 
                      },
                      { 
                        key: 'external', 
                        label: <span style={{ fontWeight: chatFilter === 'external' ? 700 : 400, color: chatFilter === 'external' ? '#0e7490' : 'inherit' }}>External</span>, 
                        onClick: () => setChatFilter('external') 
                      },
                      { 
                        key: 'ai', 
                        label: <span style={{ fontWeight: chatFilter === 'ai' ? 700 : 400, color: chatFilter === 'ai' ? '#7c3aed' : 'inherit' }}>🤖 AI</span>, 
                        onClick: () => setChatFilter('ai') 
                      },
                      { 
                        key: 'bot', 
                        label: <span style={{ fontWeight: chatFilter === 'bot' ? 700 : 400, color: chatFilter === 'bot' ? '#16a34a' : 'inherit' }}>🔀 Bot</span>, 
                        onClick: () => setChatFilter('bot') 
                      }
                    ]
                  }}
                  trigger={['click']}
                  placement="bottomRight"
                >
                  <Button 
                    type="text" 
                    icon={<MoreOutlined />} 
                    size="large" 
                    style={{ 
                      color: chatTheme === 'light' ? '#54656f' : '#ddd',
                      padding: windowWidth < 480 ? '4px 6px' : '4px 8px',
                      minWidth: windowWidth < 480 ? 36 : 40
                    }} 
                  />
                </Dropdown>
              </div>
            </div>

            {/* Suppressed-automation banner — shown when this contact's
                latest reply belongs to a one-way campaign. Auto-reply is
                disabled for this contact; an agent must respond manually. */}
            {selectedContact?.suppressedAutomation && (
              <div style={{
                margin: '0 16px',
                padding: '10px 14px',
                background: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: 8,
                color: '#78350f',
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <span style={{ fontSize: 16 }}>⚠️</span>
                <span>
                  <strong>Auto-reply disabled.</strong> This conversation came from a one-way campaign — the AI/bot will not respond automatically. Reply manually if needed.
                </span>
              </div>
            )}

            {/* Messages Area */}
           <div style={{ 
              flex: 1, 
              padding: windowWidth < 480 ? '12px 16px' : '16px 20px',
              background: 'transparent',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch'
            }} className={styles.messagesArea} ref={messagesAreaRef}>
              {loading ? (
                <MessageSkeleton />
              ) : conversation.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {conversationPaging.hasMore && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 10px' }}>
                      <Button
                        size="small"
                        onClick={loadOlderConversationMessages}
                        loading={loadingOlderMessages}
                        disabled={loadingOlderMessages}
                        style={{
                          borderRadius: 16,
                          fontSize: 12,
                          height: 28,
                          background: chatTheme === 'light' ? 'rgba(255,255,255,0.85)' : 'rgba(32,44,51,0.92)',
                          color: chatTheme === 'light' ? '#54656f' : '#d1d7db',
                          borderColor: chatTheme === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.12)'
                        }}
                      >
                        Load older messages
                      </Button>
                    </div>
                  )}
                  {groupedMessages.map((item, idx) => {
                    if (item.type === 'date') {
                      return <DateSeparator key={`date-${idx}`} date={item.date} />;
                    }
                    if (item.type === 'section') {
                      return <ChatSectionDivider key={`section-${idx}`} owner={item.owner} label={item.label} chatTheme={chatTheme} />;
                    }
                    const msg = item.data;
                    return (
                    <div
                      key={msg.id}
                      ref={idx === groupedMessages.length - 1 ? messagesEndRef : null}
                      style={{
                        display: 'flex',
                        justifyContent: msg.fromUser ? 'flex-start' : 'flex-end',
                        alignItems: 'flex-end',
                        gap: 8,
                        marginBottom: 4
                      }}
                    >
                      <div
                        className={styles.msgBubble}
                        style={{
                          maxWidth: windowWidth < 480 ? 'calc(100vw - 50px)' : '60%',
                          padding: '8px 12px 6px 12px',
                          borderRadius: msg.fromUser ? '8px 8px 8px 0' : '8px 8px 0 8px',
                          background: msg.fromUser 
                            ? CHAT_THEMES[colorTheme][chatTheme].receivedBubble
                            : CHAT_THEMES[colorTheme][chatTheme].sentBubble,
                          boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
                          position: 'relative',
                          wordWrap: 'break-word'
                        }}
                      >
                        {/* Template Reply Context - Shows the template this message is replying to.
                            Only show when messageWamid is present — this confirms the backend traced the reply
                            to the specific sent message via WAMID lookup.
                            Without messageWamid the backend used a "last campaign" fallback which is unreliable
                            and would show the wrong campaign name. */}
                        {msg.repliedToTemplate && msg.repliedToTemplate.messageWamid && (
                          <div
                            style={{
                              background: chatTheme === 'light' 
                                ? 'rgba(0, 0, 0, 0.05)' 
                                : 'rgba(255, 255, 255, 0.08)',
                              borderLeft: `3px solid ${CHAT_THEMES[colorTheme][chatTheme].accent}`,
                              borderRadius: '4px',
                              padding: '6px 10px',
                              marginBottom: '8px',
                              cursor: 'pointer'
                            }}
                          >
                            <div style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: CHAT_THEMES[colorTheme][chatTheme].accent,
                              marginBottom: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4
                            }}>
                              <span style={{ fontSize: 10 }}>📋</span>
                              <span>Replied to: {getRepliedToTemplateLabel(msg.repliedToTemplate)}</span>
                            </div>
                            {msg.repliedToTemplate.content && (
                              <div style={{
                                fontSize: 12,
                                color: msg.fromUser 
                                  ? (chatTheme === 'light' ? '#667781' : '#8696a0')
                                  : (chatTheme === 'light' ? 'rgba(255, 255, 255, 0.75)' : 'rgba(255, 255, 255, 0.7)'),
                                lineHeight: 1.3,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                fontStyle: 'italic'
                              }}>
                                {msg.repliedToTemplate.content.length > 100 
                                  ? msg.repliedToTemplate.content.substring(0, 100) + '...' 
                                  : msg.repliedToTemplate.content}
                              </div>
                            )}
                          </div>
                        )}
                        {/* Template Message Badge - shows for outgoing template messages */}
                        {msg.isTemplate && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            marginBottom: 6,
                            padding: '3px 8px',
                            background: 'rgba(255, 255, 255, 0.15)',
                            borderRadius: 4,
                            width: 'fit-content'
                          }}>
                            <span style={{ fontSize: 10 }}>📋</span>
                            <span style={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: 'rgba(171, 173, 37, 0.9)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              {msg.templateName || 'Template'}
                            </span>
                          </div>
                        )}
                        {/* External API badge removed from bubbles — visible via filter */}
                        {msg.isFlowResponse && (
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            marginBottom: 6,
                            padding: '3px 8px',
                            borderRadius: 999,
                            background: msg.fromUser
                              ? (chatTheme === 'light' ? 'rgba(0, 128, 105, 0.10)' : 'rgba(0, 128, 105, 0.22)')
                              : 'rgba(255, 255, 255, 0.18)',
                            color: msg.fromUser
                              ? (chatTheme === 'light' ? '#007a5f' : '#7ee3c3')
                              : CHAT_THEMES[colorTheme][chatTheme].sentText,
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px'
                          }}>
                            Flow response
                          </div>
                        )}
                        {/* Display media based on type */}
                        {msg.mediaUrl && msg.isImage ? (
                          <div style={{ position: 'relative' }}>
                            {/* Loading overlay when status is sending */}
                            {msg.status === 'sending' && (
                              <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: msg.text ? 30 : 0,
                                background: 'rgba(0,0,0,0.4)',
                                borderRadius: 10,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1
                              }}>
                                <div style={{
                                  width: 40,
                                  height: 40,
                                  border: '3px solid rgba(255,255,255,0.3)',
                                  borderTopColor: '#fff',
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite'
                                }} />
                              </div>
                            )}
                            <img
                              src={msg.mediaUrl}
                              alt="Uploaded media"
                              loading="lazy"
                              style={{
                                maxWidth: '100%',
                                maxHeight: 280,
                                borderRadius: 10,
                                objectFit: 'cover',
                                cursor: msg.status !== 'sending' ? 'pointer' : 'default',
                                display: 'block',
                                marginBottom: msg.text && !msg.text.startsWith('📎') ? 8 : 0
                              }}
                              onClick={() => msg.status !== 'sending' && openMediaViewer(msg.mediaUrl, 'image', msg.localPreviewUrl)}
                              onError={(e) => {
                                // Try fallback to local preview if remote URL fails
                                if (msg.localPreviewUrl && e.target.src !== msg.localPreviewUrl) {
                                  console.log('⚠️ Remote URL failed, falling back to local preview');
                                  e.target.src = msg.localPreviewUrl;
                                } else {
                                  console.log('⚠️ Image failed to load, URL may have expired. Re-fetch to get fresh signed URL.');
                                  e.target.style.display = 'none';
                                  e.target.parentElement.insertAdjacentHTML('afterbegin', 
                                    '<div style="padding:12px;background:rgba(0,0,0,0.05);border-radius:8px;color:#666;font-size:12px;">📷 Image unavailable - refresh to reload</div>'
                                  );
                                }
                              }}
                            />
                            {msg.text && !msg.text.startsWith('📎') && !msg.text.startsWith('📷') && (
                              <Text style={{ 
                                fontSize: windowWidth < 480 ? 14 : 14.5, 
                                color: msg.fromUser 
                                  ? CHAT_THEMES[colorTheme][chatTheme].receivedText
                                  : CHAT_THEMES[colorTheme][chatTheme].sentText,
                                wordBreak: 'break-word',
                                lineHeight: 1.4,
                                display: 'block',
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                                fontWeight: 400
                              }}>
                                {msg.text}
                              </Text>
                            )}
                          </div>
                        ) : msg.mediaUrl && msg.isVideo ? (
                          <div>
                            <div 
                              style={{ position: 'relative', cursor: msg.status !== 'sending' ? 'pointer' : 'default' }}
                              onClick={() => msg.status !== 'sending' && openMediaViewer(msg.mediaUrl, 'video', msg.localPreviewUrl)}
                            >
                              {/* Loading overlay when status is sending */}
                              {msg.status === 'sending' && (
                                <div style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  background: 'rgba(0,0,0,0.5)',
                                  borderRadius: 10,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  zIndex: 2
                                }}>
                                  <div style={{
                                    width: 40,
                                    height: 40,
                                    border: '3px solid rgba(255,255,255,0.3)',
                                    borderTopColor: '#fff',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                  }} />
                                </div>
                              )}
                              <video
                                src={msg.mediaUrl}
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: 280,
                                  borderRadius: 10,
                                  display: 'block',
                                  marginBottom: msg.text && !msg.text.startsWith('📎') ? 8 : 0
                                }}
                                onError={(e) => {
                                  // Try fallback to local preview if remote URL fails
                                  if (msg.localPreviewUrl && e.target.src !== msg.localPreviewUrl) {
                                    console.log('⚠️ Remote video URL failed, falling back to local preview');
                                    e.target.src = msg.localPreviewUrl;
                                  } else {
                                    console.log('⚠️ Video failed to load, URL may have expired.');
                                    e.target.style.display = 'none';
                                    e.target.parentElement.insertAdjacentHTML('afterbegin', 
                                      '<div style="padding:12px;background:rgba(0,0,0,0.05);border-radius:8px;color:#666;font-size:12px;">🎥 Video unavailable - refresh to reload</div>'
                                    );
                                  }
                                }}
                              />
                              {/* Play button overlay - hide when sending */}
                              {msg.status !== 'sending' && (
                                <div style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  background: 'rgba(0,0,0,0.6)',
                                  borderRadius: '50%',
                                  width: 60,
                                  height: 60,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#fff',
                                  fontSize: 24
                                }}>
                                  ▶
                                </div>
                              )}
                            </div>
                            {msg.text && !msg.text.startsWith('📎') && !msg.text.startsWith('🎥') && (
                              <Text style={{ 
                                fontSize: windowWidth < 480 ? 14 : 14.5, 
                                color: msg.fromUser 
                                  ? CHAT_THEMES[colorTheme][chatTheme].receivedText
                                  : CHAT_THEMES[colorTheme][chatTheme].sentText,
                                wordBreak: 'break-word',
                                lineHeight: 1.4,
                                display: 'block',
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                                fontWeight: 400
                              }}>
                                {msg.text}
                              </Text>
                            )}
                          </div>
                        ) : msg.mediaUrl && msg.isAudio ? (
                          <div style={{ position: 'relative' }}>
                            {/* Loading indicator for audio */}
                            {msg.status === 'sending' && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '12px 16px',
                                background: chatTheme === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                                borderRadius: 8,
                                marginBottom: 8
                              }}>
                                <div style={{
                                  width: 20,
                                  height: 20,
                                  border: '2px solid rgba(0,0,0,0.1)',
                                  borderTopColor: '#25D366',
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite'
                                }} />
                                <span style={{ fontSize: 13, color: chatTheme === 'light' ? '#666' : '#aaa' }}>Sending audio...</span>
                              </div>
                            )}
                            {msg.status !== 'sending' && (
                              <audio
                                src={msg.mediaUrl}
                                controls
                                style={{
                                  maxWidth: '100%',
                                  marginBottom: msg.text && !msg.text.startsWith('📎') ? 8 : 0
                                }}
                                onError={(e) => {
                                  // Try fallback to local preview if remote URL fails
                                  if (msg.localPreviewUrl && e.target.src !== msg.localPreviewUrl) {
                                    console.log('⚠️ Remote audio URL failed, falling back to local preview');
                                    e.target.src = msg.localPreviewUrl;
                                  } else {
                                    console.log('⚠️ Audio failed to load, URL may have expired.');
                                    e.target.style.display = 'none';
                                    e.target.parentElement.insertAdjacentHTML('afterbegin', 
                                      '<div style="padding:12px;background:rgba(0,0,0,0.05);border-radius:8px;color:#666;font-size:12px;">🎵 Audio unavailable - refresh to reload</div>'
                                    );
                                  }
                                }}
                              />
                            )}
                            {msg.text && !msg.text.startsWith('📎') && !msg.text.startsWith('🎵') && (
                              <Text style={{ 
                                fontSize: windowWidth < 480 ? 14 : 14.5, 
                                color: msg.fromUser 
                                  ? CHAT_THEMES[colorTheme][chatTheme].receivedText
                                  : CHAT_THEMES[colorTheme][chatTheme].sentText,
                                wordBreak: 'break-word',
                                lineHeight: 1.4,
                                display: 'block',
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                                fontWeight: 400
                              }}>
                                {msg.text}
                              </Text>
                            )}
                          </div>
                        ) : msg.mediaUrl && msg.isDocument ? (
                          <div>
                            <a 
                              href={msg.mediaUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '8px 12px',
                                background: chatTheme === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                                borderRadius: 8,
                                color: msg.fromUser 
                                  ? CHAT_THEMES[colorTheme][chatTheme].receivedText
                                  : CHAT_THEMES[colorTheme][chatTheme].sentText,
                                textDecoration: 'none',
                                marginBottom: msg.text && !msg.text.startsWith('📎') ? 8 : 0
                              }}
                            >
                              <PaperClipOutlined />
                              <span>{msg.fileName || 'Document'}</span>
                            </a>
                            {msg.text && !msg.text.startsWith('📎') && (
                              <Text style={{ 
                                fontSize: windowWidth < 480 ? 14 : 14.5, 
                                color: msg.fromUser 
                                  ? CHAT_THEMES[colorTheme][chatTheme].receivedText
                                  : CHAT_THEMES[colorTheme][chatTheme].sentText,
                                wordBreak: 'break-word',
                                lineHeight: 1.4,
                                display: 'block',
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                                fontWeight: 400
                              }}>
                                {msg.text}
                              </Text>
                            )}
                          </div>
                        ) : isImageUrl(msg.text) ? (
  <img
    src={msg.text}
    alt="Attached media"
    loading="lazy"
    style={{
      maxWidth: '100%',
      maxHeight: 280,
      borderRadius: 10,
      objectFit: 'cover',
      cursor: 'pointer'
    }}
    onClick={() => openMediaViewer(msg.text, 'image')}
    onError={(e) => {
      console.log('⚠️ Image URL failed to load');
      e.target.style.display = 'none';
      e.target.parentElement?.insertAdjacentHTML('afterbegin', 
        '<div style="padding:12px;background:rgba(0,0,0,0.05);border-radius:8px;color:#666;font-size:12px;">📷 Image unavailable</div>'
      );
    }}
  />
) : (
  <Text style={{ 
    fontSize: windowWidth < 480 ? 14 : 14.5, 
    color: msg.fromUser 
      ? CHAT_THEMES[colorTheme][chatTheme].receivedText
      : CHAT_THEMES[colorTheme][chatTheme].sentText,
    wordBreak: 'break-word',
    whiteSpace: msg.isFlowResponse ? 'pre-wrap' : 'normal',
    lineHeight: 1.4,
    display: 'block',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
    fontWeight: 400
  }}>
    {msg.text}
  </Text>
)}

                        
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'flex-end',
                          gap: 3,
                          marginTop: 2,
                          fontSize: windowWidth < 480 ? 10 : 11,
                          color: msg.fromUser 
                            ? (chatTheme === 'light' ? '#667781' : '#8696a0')
                            : CHAT_THEMES[colorTheme][chatTheme].sentTimestamp
                        }}>
                          <span style={{ opacity: 0.9 }}>{formatTime(msg.timestamp)}</span>
                          {!msg.fromUser && (
                            <span style={{ 
                              marginLeft: 2,
                              display: 'inline-flex',
                              alignItems: 'center'
                            }}>
                              {renderMessageStatus(msg.status || 'sent', colorTheme, chatTheme, windowWidth)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '100%',
                  flexDirection: 'column',
                  gap: 16
                }}>
                  <MessageOutlined style={{ fontSize: windowWidth < 480 ? 48 : 64, color: chatTheme === 'light' ? '#ddd' : '#444' }} />
                  <Text style={{ color: chatTheme === 'light' ? '#999' : '#aaa', fontSize: windowWidth < 480 ? 14 : 16 }}>
                    No messages yet
                  </Text>
                </div>
              )}
            </div>

            {/* Message Input */}
            <div style={{ 
              padding: windowWidth < 480 ? '10px 12px' : '16px 20px', 
              background: chatTheme === 'light' ? 'rgba(255,255,255,0.95)' : 'rgba(26,26,26,0.95)',
              backdropFilter: 'blur(10px)',
              borderTop: '1px solid ' + (chatTheme === 'light' ? '#e5e5e5' : '#333')
            }} className={styles.stickyInput}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: windowWidth < 480 ? 6 : 8,
                background: chatTheme === 'light' ? '#f0f2f5' : '#2a2a2a',
                borderRadius: 24,
                padding: windowWidth < 480 ? '4px 8px' : '6px 12px',
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
                minHeight: 48,
              }}>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                  onChange={handleFileSelect}
                />
                <Button 
                  type="text" 
                  icon={<PaperClipOutlined />} 
                  onClick={handleAttachmentClick}
                  loading={uploadingMedia}
                  disabled={!selectedContact || uploadingMedia || !inboxWriteAllowed}
                  style={{ 
                    color: uploadingMedia 
                      ? CHAT_THEMES[colorTheme][chatTheme].accent 
                      : (chatTheme === 'light' ? '#54656f' : '#ddd'),
                    padding: windowWidth < 480 ? '4px' : '4px 8px',
                    minWidth: windowWidth < 480 ? 32 : 40,
                    flexShrink: 0
                  }}
                  size="large"
                  title="Attach file"
                />
                
                <Input.TextArea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder={inboxWriteAllowed ? "Type a message" : "You have read-only access"}
                  disabled={!inboxWriteAllowed}
                  autoSize={{ minRows: 1, maxRows: windowWidth < 480 ? 4 : 5 }}
                  bordered={false}
                  className={chatTheme === 'dark' ? styles.darkThemePlaceholder : ''}
                  style={{ 
                    flex: 1,
                    alignSelf: 'center',
                    fontSize: 15,
                    lineHeight: '22px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: chatTheme === 'light' ? '#111b21' : '#fff',
                    padding: '0 4px',
                    margin: '0',
                    resize: 'none',
                    boxShadow: 'none !important',
                    outline: 'none !important',
                    WebkitBoxShadow: 'none',
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendReply();
                    }
                  }}
                />
                
                <div ref={emojiPickerRef} style={{ position: 'relative', flexShrink: 0 }}>
                  {showEmojiPicker && (
                    <div style={{
                      position: 'fixed',
                      bottom: windowWidth < 480 ? 60 : 80,
                      right: windowWidth < 480 ? 10 : 20,
                      left: windowWidth < 480 ? 10 : 'auto',
                      zIndex: 1000,
                      boxShadow: '0 5px 30px rgba(0,0,0,0.3)',
                      borderRadius: 12,
                      overflow: 'hidden'
                    }}>
                      {/* Close button */}
                      <Button
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={() => setShowEmojiPicker(false)}
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1001,
                          background: chatTheme === 'light' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.5)',
                          borderRadius: '50%',
                          width: 28,
                          height: 28,
                          minWidth: 28,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: chatTheme === 'light' ? '#666' : '#fff'
                        }}
                        size="small"
                      />
                      <EmojiPicker
                        onEmojiClick={(emojiData) => {
                          handleEmojiSelect(emojiData.emoji);
                          if (windowWidth < 480) setShowEmojiPicker(false);
                        }}
                        theme={chatTheme === 'light' ? 'light' : 'dark'}
                        width={windowWidth < 480 ? 'calc(100vw - 20px)' : 350}
                        height={windowWidth < 480 ? 320 : 400}
                        searchPlaceholder="Search emoji..."
                        previewConfig={{ showPreview: false }}
                        skinTonesDisabled
                        lazyLoadEmojis
                      />
                    </div>
                  )}
                  <Button 
                    type="text" 
                    icon={<SmileOutlined />} 
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    style={{ 
                      color: showEmojiPicker 
                        ? CHAT_THEMES[colorTheme][chatTheme].accent 
                        : (chatTheme === 'light' ? '#54656f' : '#ddd'),
                      padding: windowWidth < 480 ? '4px' : '4px 8px',
                      minWidth: windowWidth < 480 ? 32 : 40
                    }}
                    size="large"
                  />
                </div>
                
                <Button
                  type="text"
                  icon={<SendOutlined />}
                  onClick={handleSendReply}
                  loading={sendingMessage}
                  disabled={!reply.trim() || !inboxWriteAllowed}
                  size="large"
                  style={{
                    color: reply.trim() ? CHAT_THEMES[colorTheme][chatTheme].accent : (chatTheme === 'light' ? '#bbb' : '#555'),
                    transition: 'all 0.2s ease',
                    padding: windowWidth < 480 ? '4px' : '4px 8px',
                    minWidth: windowWidth < 480 ? 32 : 40,
                    flexShrink: 0
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            flexDirection: 'column',
            gap: 24,
            background: chatTheme === 'light' 
              ? 'linear-gradient(180deg, #f8f9ff 0%, #fff 100%)' 
              : 'linear-gradient(180deg, #1a1a2e 0%, #1a1a1a 100%)',
            padding: windowWidth < 480 ? '16px' : 0
          }}>
            <NoChatLogoImage
              src={messageOSLogo}
              width={windowWidth < 480 ? 160 : 220}
              chatTheme={chatTheme}
            />
            <Text style={{ 
              color: chatTheme === 'light' ? '#666' : '#aaa', 
              fontSize: windowWidth < 480 ? 14 : 16, 
              textAlign: 'center',
              maxWidth: 320,
              lineHeight: 1.7,
              padding: windowWidth < 480 ? '0 16px' : 0
            }}>
              Select a chat to see the conversation here
            </Text>
          </div>
        )}
      </div>
      
      {/* Media Preview Modal */}
      <Modal
        title={
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: chatTheme === 'light' ? '#1a1a1a' : '#fff'
          }}>
            <PaperClipOutlined />
            <span>Send Media</span>
          </div>
        }
        open={showMediaPreview}
        onCancel={closeMediaPreview}
        footer={[
          <Button key="cancel" onClick={closeMediaPreview}>
            Cancel
          </Button>,
          <Button
            key="send"
            type="primary"
            loading={uploadingMedia}
            onClick={handleSendMedia}
            disabled={!inboxWriteAllowed}
            style={{
              background: CHAT_THEMES[colorTheme][chatTheme].accent,
              borderColor: CHAT_THEMES[colorTheme][chatTheme].accent
            }}
          >
            Send
          </Button>
        ]}
        width={windowWidth < 480 ? '95%' : windowWidth < 768 ? '90%' : 600}
        zIndex={1500}
        style={{ 
          top: windowWidth < 480 ? 10 : windowWidth < 768 ? 20 : 50,
          maxHeight: windowWidth < 480 ? '95vh' : windowWidth < 768 ? '90vh' : '80vh'
        }}
        bodyStyle={{
          background: chatTheme === 'light' ? '#fff' : '#1a1a1a',
          color: chatTheme === 'light' ? '#1a1a1a' : '#fff',
          maxHeight: windowWidth < 480 ? '70vh' : windowWidth < 768 ? '60vh' : '500px',
          overflowY: 'auto'
        }}
      >
        {previewFile && (
          <div style={{ textAlign: 'center' }}>
            {/* File Info */}
            <div style={{
              background: chatTheme === 'light' ? '#f5f5f5' : '#2a2a2a',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '16px',
              textAlign: 'left'
            }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                📎 {previewFile.name}
              </div>
              <div style={{
                fontSize: 12,
                color: chatTheme === 'light' ? '#666' : '#aaa'
              }}>
                {(previewFile.size / 1024 / 1024).toFixed(2)} MB • {previewFile.type}
              </div>
            </div>

            {/* Preview Content */}
            {previewFile.type.startsWith('image/') && (
              <div style={{ marginBottom: '16px' }}>
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    borderRadius: '8px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            )}

            {previewFile.type.startsWith('video/') && (
              <div style={{ marginBottom: '16px' }}>
                <video
                  src={previewUrl}
                  controls
                  style={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    borderRadius: '8px'
                  }}
                />
              </div>
            )}

            {previewFile.type.startsWith('audio/') && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  background: chatTheme === 'light' ? '#f0f0f0' : '#333',
                  padding: '20px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: '16px'
                }}>
                  <div style={{ fontSize: 48 }}>🎵</div>
                  <audio
                    src={previewUrl}
                    controls
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            )}

            {!previewFile.type.startsWith('image/') && 
             !previewFile.type.startsWith('video/') && 
             !previewFile.type.startsWith('audio/') && (
              <div style={{
                background: chatTheme === 'light' ? '#f0f0f0' : '#333',
                padding: '40px',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📄</div>
                <div style={{ fontWeight: 600 }}>
                  Document Preview
                </div>
                <div style={{
                  fontSize: 14,
                  color: chatTheme === 'light' ? '#666' : '#aaa',
                  marginTop: 8
                }}>
                  Ready to send {previewFile.name}
                </div>
              </div>
            )}

            {/* Caption Input */}
            <Input.TextArea
              placeholder="Add a caption (optional)"
              value={mediaCaption}
              onChange={(e) => setMediaCaption(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 4 }}
              style={{
                background: chatTheme === 'light' ? '#fff' : '#2a2a2a',
                border: `1px solid ${chatTheme === 'light' ? '#d9d9d9' : '#444'}`,
                color: chatTheme === 'light' ? '#1a1a1a' : '#fff'
              }}
              className={chatTheme === 'dark' ? styles.darkThemePlaceholder : ''}
            />

            <div style={{
              marginTop: '16px',
              fontSize: 12,
              color: chatTheme === 'light' ? '#666' : '#aaa',
              textAlign: 'left'
            }}>
              Send to: {selectedContact?.name} ({selectedContact?.phoneNumber})
            </div>
          </div>
        )}
      </Modal>

      {/* Media Viewer Modal (Lightbox) */}
      <Modal
        open={mediaViewerOpen}
        onCancel={closeMediaViewer}
        footer={null}
        width={windowWidth < 480 ? '100%' : '90%'}
        style={{ 
          maxWidth: 900, 
          top: windowWidth < 480 ? 0 : 20,
          margin: windowWidth < 480 ? 0 : 'auto',
          padding: windowWidth < 480 ? 0 : undefined
        }}
        centered
        zIndex={2000}
        bodyStyle={{ 
          padding: 0, 
          background: 'transparent',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: windowWidth < 480 ? '100vh' : '60vh'
        }}
        closeIcon={
          <div style={{
            background: 'rgba(0,0,0,0.7)',
            borderRadius: '50%',
            width: windowWidth < 480 ? 44 : 36,
            height: windowWidth < 480 ? 44 : 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: windowWidth < 480 ? 22 : 18,
            marginTop: windowWidth < 480 ? 8 : 0,
            marginRight: windowWidth < 480 ? 8 : 0
          }}>
            ✕
          </div>
        }
        modalRender={(modal) => (
          <div style={{ background: 'transparent' }}>
            {modal}
          </div>
        )}
        styles={{
          mask: { background: 'rgba(0,0,0,0.95)', zIndex: 2000 },
          content: { background: 'transparent', boxShadow: 'none' }
        }}
      >
        {mediaViewerType === 'image' && mediaViewerUrl && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            width: '100%',
            height: '100%',
            position: 'relative'
          }}>
            {/* Top Toolbar */}
            <div style={{ 
              position: 'fixed',
              top: windowWidth < 480 ? 60 : 16,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex', 
              alignItems: 'center',
              gap: windowWidth < 480 ? 4 : 2,
              background: 'rgba(0,0,0,0.8)',
              backdropFilter: 'blur(12px)',
              padding: windowWidth < 480 ? '10px 16px' : '8px 12px',
              borderRadius: 12,
              boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
              zIndex: 2001
            }}>
              {/* Zoom Out */}
              <button
                onClick={handleZoomOut}
                disabled={imageZoom <= MIN_ZOOM}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: imageZoom <= MIN_ZOOM ? 'rgba(255,255,255,0.3)' : '#fff',
                  width: windowWidth < 480 ? 44 : 36,
                  height: windowWidth < 480 ? 44 : 36,
                  borderRadius: 8,
                  cursor: imageZoom <= MIN_ZOOM ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  fontWeight: 300,
                  transition: 'all 0.15s ease',
                  WebkitTapHighlightColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (imageZoom > MIN_ZOOM) e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
                title="Zoom Out (−)"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="8" y1="11" x2="14" y2="11"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </button>
              
              {/* Zoom Percentage Display */}
              <div 
                onClick={handleResetZoom}
                style={{
                  padding: '6px 16px',
                  minWidth: 70,
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderRadius: 6,
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
                title="Click to reset (0)"
              >
                <span style={{ 
                  color: '#fff', 
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }}>
                  {Math.round(imageZoom * 100)}%
                </span>
              </div>
              
              {/* Zoom In */}
              <button
                onClick={handleZoomIn}
                disabled={imageZoom >= MAX_ZOOM}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: imageZoom >= MAX_ZOOM ? 'rgba(255,255,255,0.3)' : '#fff',
                  width: windowWidth < 480 ? 44 : 36,
                  height: windowWidth < 480 ? 44 : 36,
                  borderRadius: 8,
                  cursor: imageZoom >= MAX_ZOOM ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  fontWeight: 300,
                  transition: 'all 0.15s ease',
                  WebkitTapHighlightColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (imageZoom < MAX_ZOOM) e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
                title="Zoom In (+)"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="11" y1="8" x2="11" y2="14"/>
                  <line x1="8" y1="11" x2="14" y2="11"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </button>

              {/* Divider */}
              <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.2)', margin: '0 8px' }} />

              {/* Fit to Screen */}
              <button
                onClick={handleResetZoom}
                style={{
                  background: imageZoom === 1 ? 'rgba(255,255,255,0.15)' : 'transparent',
                  border: 'none',
                  color: '#fff',
                  width: windowWidth < 480 ? 44 : 36,
                  height: windowWidth < 480 ? 44 : 36,
                  borderRadius: 8,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease',
                  WebkitTapHighlightColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = imageZoom === 1 ? 'rgba(255,255,255,0.15)' : 'transparent';
                }}
                title="Fit to Screen (0)"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                </svg>
              </button>

              {/* Divider */}
              <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.2)', margin: '0 8px' }} />

              {/* Download */}
              <button
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = mediaViewerUrl;
                  link.download = 'image';
                  link.target = '_blank';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  width: windowWidth < 480 ? 44 : 36,
                  height: windowWidth < 480 ? 44 : 36,
                  borderRadius: 8,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease',
                  WebkitTapHighlightColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
                title="Download"
              >
                <DownloadOutlined style={{ fontSize: windowWidth < 480 ? 20 : 18 }} />
              </button>

              {/* Open in New Tab */}
              <button
                onClick={() => window.open(mediaViewerUrl, '_blank')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  width: windowWidth < 480 ? 44 : 36,
                  height: windowWidth < 480 ? 44 : 36,
                  borderRadius: 8,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease',
                  WebkitTapHighlightColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
                title="Open in New Tab"
              >
                <svg width={windowWidth < 480 ? "20" : "18"} height={windowWidth < 480 ? "20" : "18"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                  <polyline points="15,3 21,3 21,9"/>
                  <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </button>
            </div>
            
            {/* Image Container with Pan/Zoom */}
            <div 
              ref={imageContainerRef}
              style={{
                width: '100%',
                height: windowWidth < 480 ? '70vh' : '80vh',
                marginTop: windowWidth < 480 ? 60 : 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                cursor: imageZoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
                userSelect: 'none',
                touchAction: 'none',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none'
              }}
              onWheel={handleWheelZoom}
              onMouseDown={handleDragStart}
              onMouseMove={handleDragMove}
              onMouseUp={handleDragEnd}
              onMouseLeave={handleDragEnd}
              onTouchStart={handleDragStart}
              onTouchMove={handleDragMove}
              onTouchEnd={handleDragEnd}
              onDoubleClick={handleDoubleClick}
            >
              <img
                src={mediaViewerUrl}
                alt="Media preview"
                draggable={false}
                style={{
                  transform: `scale(${imageZoom}) translate(${imagePan.x / imageZoom}px, ${imagePan.y / imageZoom}px)`,
                  transformOrigin: 'center center',
                  transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                  maxWidth: windowWidth < 480 ? '100vw' : '90vw',
                  maxHeight: windowWidth < 480 ? '70vh' : '80vh',
                  objectFit: 'contain',
                  borderRadius: windowWidth < 480 ? 0 : 4,
                  pointerEvents: 'none',
                  WebkitUserDrag: 'none'
                }}
                onError={(e) => {
                  // Try fallback URL (local preview) if remote URL fails
                  if (mediaViewerFallbackUrl && e.target.src !== mediaViewerFallbackUrl) {
                    console.log('⚠️ Remote URL failed in viewer, trying fallback');
                    setMediaViewerUrl(mediaViewerFallbackUrl);
                  } else {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<div style="color:#fff;text-align:center;padding:40px;background:rgba(255,255,255,0.05);border-radius:12px;"><div style="font-size:48px;margin-bottom:16px;">⚠️</div><div style="font-size:16px;font-weight:500;">Image failed to load</div><div style="font-size:13px;opacity:0.7;margin-top:8px;">The URL may have expired. Try refreshing.</div></div>';
                  }
                }}
              />
            </div>

            {/* Bottom hint */}
            <div style={{
              position: 'fixed',
              bottom: windowWidth < 480 ? 30 : 20,
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'rgba(255,255,255,0.6)',
              fontSize: windowWidth < 480 ? 13 : 12,
              background: 'rgba(0,0,0,0.6)',
              padding: windowWidth < 480 ? '8px 16px' : '6px 12px',
              borderRadius: 8,
              display: 'flex',
              gap: windowWidth < 480 ? 12 : 16,
              zIndex: 2001
            }}>
              {windowWidth < 480 ? (
                <>
                  <span>Tap +/− to zoom</span>
                  <span>Double-tap to fit</span>
                </>
              ) : (
                <>
                  <span>Scroll to zoom</span>
                  <span>Double-click to toggle</span>
                  {imageZoom > 1 && <span>Drag to pan</span>}
                </>
              )}
            </div>
          </div>
        )}
        {mediaViewerType === 'video' && mediaViewerUrl && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: windowWidth < 480 ? '100vh' : 'auto',
            padding: windowWidth < 480 ? '60px 0 80px' : 0
          }}>
            <video
              src={mediaViewerUrl}
              controls
              autoPlay
              playsInline
              webkit-playsinline="true"
              style={{
                width: windowWidth < 480 ? '100%' : 'auto',
                maxWidth: '100%',
                maxHeight: windowWidth < 480 ? '70vh' : '75vh',
                borderRadius: windowWidth < 480 ? 0 : 8,
                background: '#000'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<div style="color:#fff;text-align:center;padding:40px;background:rgba(255,255,255,0.05);border-radius:12px;"><div style="font-size:48px;margin-bottom:16px;">⚠️</div><div style="font-size:16px;font-weight:500;">Video failed to load</div><div style="font-size:13px;opacity:0.7;margin-top:8px;">The URL may have expired. Try refreshing.</div></div>';
              }}
            />
            <div style={{ 
              marginTop: windowWidth < 480 ? 20 : 16, 
              display: 'flex', 
              gap: 12,
              position: windowWidth < 480 ? 'fixed' : 'relative',
              bottom: windowWidth < 480 ? 30 : 'auto'
            }}>
              <Button 
                type="primary"
                icon={<DownloadOutlined />}
                size={windowWidth < 480 ? 'large' : 'middle'}
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = mediaViewerUrl;
                  link.download = 'video';
                  link.target = '_blank';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                style={{ 
                  background: CHAT_THEMES[colorTheme][chatTheme].accent,
                  height: windowWidth < 480 ? 44 : 32,
                  padding: windowWidth < 480 ? '0 24px' : '0 15px'
                }}
              >
                Download
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Label Contact Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TagOutlined style={{ color: '#25d366', fontSize: 16 }} />
            <span style={{ fontSize: 15 }}>Label Contact</span>
          </div>
        }
        open={labelContactModalOpen}
        onCancel={() => { setLabelContactModalOpen(false); labelContactForm.resetFields(); }}
        footer={null}
        width={windowWidth < 480 ? '95vw' : 420}
        style={{ top: windowWidth < 480 ? 16 : undefined }}
        styles={{ body: { padding: '12px 16px' } }}
        zIndex={1200}
        destroyOnClose
      >
        {selectedContact && (() => {
          const currentLabels = selectedContact.labels || [];
          const hasLabel = currentLabels.length > 0;
          return (
            <div style={{ marginBottom: 10, padding: '8px 12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{selectedContact.name}</div>
                <div style={{ fontSize: 11, color: '#666' }}>{selectedContact.phoneNumber}</div>
              </div>
              {hasLabel && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Tag color="green" style={{ margin: 0, fontSize: 11 }}>{currentLabels[0]}</Tag>
                </div>
              )}
            </div>
          );
        })()}

        <Spin spinning={labelContactBooksLoading}>
          <Form
            form={labelContactForm}
            layout="vertical"
            onFinish={handleLabelContactSubmit}
            style={{ marginBottom: 0 }}
          >
            <Form.Item
              name="labelTypes"
              label={<span style={{ fontSize: 13, fontWeight: 600 }}>Select Label</span>}
              style={{ marginBottom: 10 }}
            >
              <Checkbox.Group style={{ width: '100%' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px' }}>
                  {predefinedLabels.map((lbl) => (
                    <Checkbox key={lbl.value} value={lbl.value} style={{ margin: 0 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13 }}>
                        <span style={{ color: '#64748b' }}>{lbl.icon}</span>
                        <span>{lbl.label}</span>
                      </span>
                    </Checkbox>
                  ))}
                </div>
              </Checkbox.Group>
            </Form.Item>

            <Form.Item
              name="customLabel"
              label={<span style={{ fontSize: 13, fontWeight: 600 }}>Custom Label <span style={{ fontWeight: 400, color: '#999' }}>(optional)</span></span>}
              style={{ marginBottom: 12 }}
            >
              <Input placeholder="e.g. wholesale, premium..." maxLength={30} style={{ borderRadius: 8, fontSize: 13 }} />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button size="small" onClick={() => { setLabelContactModalOpen(false); labelContactForm.resetFields(); }}>
                  Cancel
                </Button>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => {
                    const checked = getFieldValue('labelTypes') || [];
                    const custom = (getFieldValue('customLabel') || '').trim();
                    const hasSelection = checked.length > 0 || custom.length > 0;
                    const hasExisting = (selectedContact?.labels || []).length > 0;
                    const btnLabel = !hasSelection && hasExisting ? 'Remove Labels' : hasExisting ? 'Update Label' : 'Assign Label';
                    const btnStyle = !hasSelection && hasExisting
                      ? { background: '#ff4d4f', borderColor: '#ff4d4f' }
                      : { background: '#25d366', borderColor: '#25d366' };
                    return (
                      <Button size="small" type="primary" htmlType="submit" loading={labelContactLoading} style={btnStyle}>
                        {btnLabel}
                      </Button>
                    );
                  }}
                </Form.Item>
              </div>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </div>
    </>
  );
};

export default InboxPage;